/**
 * PolicyConfigPageV2 — proper v2 redesign of Policy Library (G116).
 *
 * Hybrid pattern: TabNav for the 8 v1 tabs + canonical inventory pattern
 * (FacetRail + Table + Slideout) for the Policy Skills tab. Other tabs
 * (Policy Library, Control Config, Framework Coverage, Control Drift,
 * Control Scorecard, Data Classification, Pattern Library) keep their
 * v1 panels — they're already complex sub-pages with their own state.
 *
 * Reuses: api.getPolicyConfigs / getPolicyConfig / createPolicyConfig /
 * updatePolicyConfig + the v1 panel components.
 *
 * v1 stays as fallback via /policy-config?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthPoliciesEnabled } from '../utils/featureFlags';
import { usePersistedState } from '../hooks/usePersistedState';
import * as api from '../services/api';

import PolicySkillWizard from '../components/policy/PolicySkillWizard';
import EnforcementReviewPanel from '../components/policy/EnforcementReviewPanel';
import DataClassificationRuleEditor from '../components/policy/DataClassificationRuleEditor';
// G432/G433 — Data Classification + Pattern Library are now inline V2
// inventories (DataClassificationInventory / PatternLibraryInventory below).
// The legacy panels stay on disk for reference / quick rollback via the
// ?legacy=1 path through ResolvedPolicyConfigPage; the v1 imports are no
// longer wired into the v2 tab routing.

import {
  RefreshCw, Plus, Shield, FileCheck, Layers, Target, TrendingUp,
  BarChart3, Database, BookOpen, Sparkles, Activity, X, Eye, Edit3,
  Trash2, Archive, AlertTriangle, CheckCircle, Globe, FileText,
  ArrowRight, FileCode2, ChevronRight,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TabNav,
} from '../components/v2';
import { FrameworkBindingTransitionsTimeline } from '../components/v2/compliance';
import { ControlEvidencePanel } from '../components/v2/compliance/ControlEvidencePanel';
/* Step 4 (Platform-Eng workflow) — Rego authoring slideout. Mounted
 * from the Policy Skills tab; closes the Tier-1 gap of having no UI
 * to author a Rego module (previously curl-only). */
import { RegoEditorSlideout } from '../components/v2/policy/RegoEditorSlideout';
/* Step 7 (Platform-Eng workflow) — OPA decision viewer. Renders
 * the new "Decisions" tab; closes the second Tier-1 gap (denied
 * SRE calls were yielding generic 403s with no named-reason
 * surface because policy_decisions rows had no React surface). */
import { PolicyDecisionsInventory } from '../components/v2/policy/PolicyDecisionsInventory';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import PolicyConfigPage from './PolicyConfigPage';

const STATUS_OPTIONS = [
  { key: 'draft',    label: 'Draft',    tone: 'muted' },
  { key: 'testing',  label: 'Testing',  tone: 'warning' },
  { key: 'active',   label: 'Active',   tone: 'success' },
  { key: 'paused',   label: 'Paused',   tone: 'warning' },
  { key: 'archived', label: 'Archived', tone: 'muted' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));

const TABS = [
  { id: 'configurations',  label: 'Policy Skills' },
  /* Step 7 (Platform-Eng workflow) — OPA decision viewer. Sits
   * next to Policy Skills because the SRE asking "why was my call
   * denied?" goes here, not to a different page. */
  { id: 'decisions',       label: 'Decisions' },
  { id: 'policy-library',  label: 'Policy Library' },
  { id: 'control-config',  label: 'Control Config' },
  { id: 'framework-coverage', label: 'Framework Coverage' },
  { id: 'control-drift',   label: 'Control Drift' },
  { id: 'control-scorecard', label: 'Control Scorecard' },
  /* P0-1 Sprint 2 — Per-control evidence dashboard. Auditors and
   * compliance reviewers use this to answer "for framework X, list
   * every control we're enforcing today and when each one entered
   * enforcement." Backed by GET /compliance-center/control-evidence
   * which fans the tenant's framework_binding rows out by their
   * control_mappings JSONB array. */
  { id: 'control-evidence', label: 'Control Evidence' },
  { id: 'classification',  label: 'Data Classification' },
  { id: 'patterns',        label: 'Pattern Library' },
];

/* ── Inventory: Policy Skills tab ───────────────────────────────── */
function PolicySkillsInventory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();

  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, statsRes] = await Promise.all([
        api.getPolicyConfigs?.()?.catch(() => null),
        api.getPolicyConfigStats?.()?.catch(() => null),
      ]);
      const items = Array.isArray(cfgRes?.data?.items) ? cfgRes.data.items
                  : Array.isArray(cfgRes?.items) ? cfgRes.items
                  : Array.isArray(cfgRes?.data) ? cfgRes.data
                  : Array.isArray(cfgRes) ? cfgRes : [];
      setConfigs(items);
      setStats(statsRes?.data || statsRes || {});
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({
    status:    new Set(),
    framework: new Set(),
  }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ status: new Set(), framework: new Set() });

  /* G212 — Backend PolicySkillConfiguration entity uses `activationStatus`
   * (string, lowercase: draft|testing|active|paused|archived) and `skillName`.
   * There is no `status` / `displayName` / `tier` / `frameworks[]` field on
   * the wire shape — V2 was reading non-existent fields, which is why every
   * row showed "unknown" and all KPI tiles read 0. The framework binding is
   * a single FK via `frameworkBindingId`, not a list. We strip the tier
   * facet entirely (no backend signal) and derive framework facet options
   * from frameworkBindingId. Status is read via the canonical accessor
   * below so we tolerate both shapes during the migration. */
  const statusOf = (c) => (c?.activationStatus || c?.status || '').toLowerCase();
  const nameOf   = (c) => c?.skillName || c?.displayName || c?.name || c?.id;
  const frameworkIdOf = (c) => c?.frameworkBindingId || c?.frameworkId || null;

  const frameworks = useMemo(() => {
    const s = new Set();
    for (const c of configs) {
      const f = frameworkIdOf(c);
      if (f) s.add(f);
    }
    return Array.from(s).sort();
  }, [configs]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status',    label: 'Status',    options: STATUS_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    { key: 'framework', label: 'Framework', options: frameworks.map(f => ({ key: f, label: String(f).slice(0, 8) + '…' })) },
  ]), [frameworks]);

  const filtered = useMemo(() => configs.filter(c => {
    if (facets.status.size > 0 && !facets.status.has(statusOf(c))) return false;
    if (facets.framework.size > 0) {
      const f = frameworkIdOf(c);
      if (!f || !facets.framework.has(f)) return false;
    }
    return true;
  }), [configs, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = configs.filter(c => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (og.key === 'status') { if (!sel.has(statusOf(c))) return false; }
            else if (og.key === 'framework') {
              const f = frameworkIdOf(c);
              if (!f || !sel.has(f)) return false;
            }
          }
          if (g.key === 'status')    return statusOf(c) === opt.key;
          if (g.key === 'framework') return frameworkIdOf(c) === opt.key;
          return false;
        }).length;
      }
    }
    return counts;
  }, [configs, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => { try { return localStorage.getItem('policy-density') || 'comfortable'; } catch { return 'comfortable'; } });
  const cycleDensity = () => { const n = density === 'comfortable' ? 'compact' : 'comfortable'; setDensity(n); try { localStorage.setItem('policy-density', n); } catch {} };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  const [showWizard, setShowWizard] = useState(false);
  /* G208 — When set, opens the PolicySkillWizard pre-loaded with this
   * existing config for in-place editing (calls api.updatePolicyConfig). */
  const [editingConfig, setEditingConfig] = useState(null);
  /* POLICY-PHASE2 — stage key the bridge deep-link requested (extract|bind),
   * threaded into the wizard as initialStage so it lands on the right step. */
  const [wizardInitialStage, setWizardInitialStage] = useState(null);
  /* When the wizard is opened FROM a specific agent (deep-link carries agentId),
   * scope the wizard's policy-skill picker to that agent only — it must never
   * list other agents' policy skills. Null = tenant-wide (Policies-page entry). */
  const [wizardScopeAgentId, setWizardScopeAgentId] = useState(null);
  // Agent the operator came FROM (deep-link), so we can offer a precise
  // "Back to <agent> — Build phase" return link.
  const [wizardBackAgentName, setWizardBackAgentName] = useState(null);

  const openWizardForCreate = () => { setEditingConfig(null); setWizardInitialStage(null); setWizardScopeAgentId(null); setWizardBackAgentName(null); setShowWizard(true); };
  const openWizardForEdit = (cfg, stage = null, scopeAgentId = null, backAgentName = null) => {
    if (!cfg) return;
    setEditingConfig(cfg);
    setWizardInitialStage(stage);
    setWizardScopeAgentId(scopeAgentId);
    setWizardBackAgentName(backAgentName);
    setShowWizard(true);
  };
  const closeWizard = () => {
    setShowWizard(false);
    setEditingConfig(null);
    setWizardInitialStage(null);
    setWizardScopeAgentId(null);
    setWizardBackAgentName(null);
  };
  // Return exactly where the operator came from: the agent's Promote → Build phase.
  const backToAgentBuild = () => {
    const id = wizardScopeAgentId;
    closeWizard();
    if (id) navigate(`/agents/${id}?tab=promote&phase=build`);
  };

  /* POLICY-PHASE2 — bridge from the agent's policy_skill panel:
   * /policy-config?skillId=<id>&stage=extract|bind. Resolve (or lazily
   * create) the PolicySkillConfiguration for that skill, then open the
   * wizard pre-positioned at the requested stage. Consumed once and cleared
   * so back-navigation doesn't re-open. Waits until the inventory has loaded
   * so an existing config is matched in-memory before falling back to a
   * by-skill fetch / create. */
  const bridgeConsumed = React.useRef(false);
  useEffect(() => {
    const skillId = searchParams.get('skillId');
    const stage = searchParams.get('stage');
    const deepLinkAgentId = searchParams.get('agentId');
    const deepLinkAgentName = searchParams.get('agentName');
    if (!skillId || loading || bridgeConsumed.current) return;
    bridgeConsumed.current = true;
    (async () => {
      try {
        let cfg = configs.find((c) => c.skillId === skillId) || null;
        if (!cfg) {
          const bySkill = await api.getPolicyConfigBySkill?.(skillId)?.catch(() => null);
          cfg = bySkill?.data || (bySkill && !bySkill.ok ? null : bySkill) || null;
        }
        if (!cfg) {
          const created = await api.createPolicyConfig?.({ skillId })?.catch(() => null);
          cfg = created?.data || created || null;
        }
        if (cfg) {
          openWizardForEdit(cfg, stage, deepLinkAgentId || cfg.agentId || null, deepLinkAgentName || null);
        } else {
          addToast?.({ title: 'Could not open the policy wizard for that skill', tone: 'warning' });
        }
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('skillId');
        next.delete('stage');
        next.delete('agentId');
        next.delete('agentName');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, loading, configs, addToast, setSearchParams]);

  /* Step 4 — Rego authoring slideout state. Independent of the
   * legacy PolicySkillWizard so the wizard path is untouched
   * (zero regression). regoEditTarget is null in CREATE mode and
   * carries the existing PolicyRule row in EDIT mode (currently
   * unused from this page — exposed for forward compatibility
   * when an in-slideout rule list lands). */
  const [regoSlideoutOpen, setRegoSlideoutOpen] = useState(false);
  const [regoEditTarget, setRegoEditTarget] = useState(null);
  const openRegoCreate = () => { setRegoEditTarget(null); setRegoSlideoutOpen(true); };
  const closeRegoSlideout = () => { setRegoSlideoutOpen(false); setRegoEditTarget(null); };
  const onRegoSaved = useCallback(() => {
    addToast?.({ title: 'Rego rule saved', tone: 'success' });
    reload();
  }, [addToast, reload]);

  const kpis = useMemo(() => ({
    total:    configs.length,
    active:   configs.filter(c => statusOf(c) === 'active').length,
    testing:  configs.filter(c => statusOf(c) === 'testing').length,
    drafts:   configs.filter(c => statusOf(c) === 'draft').length,
  }), [configs]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total policies" value={kpis.total} tone="cyan" />
        <MetricCard label="Active"          value={kpis.active} tone="success" />
        <MetricCard label="Testing"         value={kpis.testing} tone="warning" />
        <MetricCard label="Drafts"          value={kpis.drafts} tone="muted" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
        {/* Step 4 (Platform-Eng workflow) — Rego authoring entry
            point. Distinct from the legacy 5-stage Policy Skill
            wizard because the use cases don't overlap: the wizard
            compiles policies from documents + framework controls;
            this slideout writes a single Rego module + binding
            directly. */}
        <Button variant="secondary" size="sm" iconLeft={<FileCode2 size={13} />} onClick={openRegoCreate}>Author Rego rule</Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={openWizardForCreate}>New Policy Skill</Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Policy filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Shield size={20} />}
            title={configs.length === 0 ? 'No policies authored yet' : 'No policies match filters'}
            description={configs.length === 0
              ? 'Author your first policy skill to enforce governance rules on agent execution.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={configs.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openWizardForCreate}>New Policy Skill</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Policy skills"
            density={density}
            columns={[
              { key: 'name', header: 'Policy', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{nameOf(r)}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{(r.description || '').slice(0, 60)}</div>
                </div>
              )},
              { key: 'status', header: 'Status', render: (r) => {
                const s = statusOf(r);
                return <Pill tone={STATUS_TONE[s] || 'muted'} size="sm" dot>{s || 'draft'}</Pill>;
              }},
              { key: 'framework', header: 'Framework', render: (r) => {
                const f = frameworkIdOf(r);
                return f
                  ? <Pill tone="muted" size="sm">{String(f).slice(0, 8)}…</Pill>
                  : <span className="text-[11px] text-v2-ink-muted">—</span>;
              }},
              { key: 'rules', header: 'Rules', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink">{r.activeRuleCount ?? '—'}</span>
              )},
              { key: 'updated', header: 'Updated', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '—'}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => openWizardForEdit(r)} title="Edit">
                    <Edit3 size={13} />
                  </Button>
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="policies" />
      </div>

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${nameOf(selected)} detail` : 'Policy detail'}
        width={520}
        tabs={[
          { id: 'overview',     label: 'Overview' },
          { id: 'rules',        label: 'Rules', badge: (selected?.activeRuleCount ?? undefined) },
          { id: 'frameworks',   label: 'Framework', badge: frameworkIdOf(selected) ? 1 : undefined },
          { id: 'enforcement',  label: 'Enforcement' },
          { id: 'audit',        label: 'Audit' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              {/* G212 — Use canonical activationStatus + skillName accessors. */}
              <Pill tone={STATUS_TONE[statusOf(selected)] || 'muted'} size="sm" dot>{statusOf(selected) || 'draft'}</Pill>
              {frameworkIdOf(selected) && (
                <Pill tone="cyan" size="sm">framework bound</Pill>
              )}
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{nameOf(selected)}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description?.slice(0, 100) || '—'}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Edit3 size={13} />} onClick={() => openWizardForEdit(selected)}>Edit</Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Overview" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed">{selected.description || 'No description.'}</p>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px] mt-3">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Updated</dt><dd className="text-v2-ink mt-0.5">{selected.updatedAt ? new Date(selected.updatedAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Active rules</dt><dd className="text-v2-ink mt-0.5">{selected.activeRuleCount ?? '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Activated by</dt><dd className="text-v2-ink mt-0.5">{selected.activatedBy || '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'rules' && (
          <Card padding="sm">
            <Card.Header title="Enforcement rules" actions={
              <div className="flex items-center gap-1">
                {/* Step 4 — secondary entry point into the Rego authoring
                    slideout from within an existing policy skill's
                    detail panel. Independent action from the legacy
                    "Edit rules" wizard path; both stay available. */}
                <Button variant="ghost" size="sm" iconLeft={<FileCode2 size={12} />} onClick={openRegoCreate}>Add Rego rule</Button>
                <Button variant="ghost" size="sm" onClick={() => openWizardForEdit(selected)}>Edit rules</Button>
              </div>
            } />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Active rules</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.activeRuleCount ?? '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Total evaluations</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.totalEvaluations?.toLocaleString?.() ?? '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Total violations</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.totalViolations?.toLocaleString?.() ?? '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Documents</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{(selected.documentIds || []).length}</dd></div>
              </dl>
              <p className="text-v2-ink-muted text-[11.5px] mt-3">Open the policy wizard to author or modify the enforcement rules and rule bindings.</p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'frameworks' && (
          <div className="space-y-4">
            <Card padding="sm">
              <Card.Header title="Bound framework" />
              <Card.Body>
                {!frameworkIdOf(selected) ? (
                  <p className="text-v2-ink-muted text-[12.5px]">Not bound to any compliance framework.</p>
                ) : (
                  <div>
                    <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">Framework binding ID</span>
                    <code className="block mt-1 text-[11.5px] font-mono-v2 text-v2-ink-secondary break-all">{frameworkIdOf(selected)}</code>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* P0-1 — Per-binding transition history. Renders the
                hash-chained audit timeline so operators reviewing this
                policy skill can see exactly when the bound framework
                moved through PENDING → SKILL_CREATED → RULES_GENERATED
                → TESTING → ACTIVE (or sideways into FAILED/ARCHIVED).
                The timeline is read-only — every state change emits a
                new row via FrameworkBindingService, so any mutation
                tried here would defeat the tamper-evident purpose. */}
            {frameworkIdOf(selected) && (
              <FrameworkBindingTransitionsTimeline
                bindingId={frameworkIdOf(selected)}
                compact
              />
            )}
          </div>
        )}
        {selected && slideoutTab === 'enforcement' && (
          /* G376 — Reuse the V1 EnforcementReviewPanel directly. It's a
             self-contained pre-deploy preview component that already wraps
             its own API calls, so V2 just composes it inside the slideout
             instead of bouncing to /policy-config?legacy=1. */
          <Card padding="sm">
            <Card.Header title="Enforcement review" />
            <Card.Body>
              <EnforcementReviewPanel policyConfig={selected} embedded />
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            <Card.Header title="Audit trail" />
            <Card.Body>
              {/* G436 — Route is /workflow-audit (see App.jsx:381); the
                  legacy /audit path was never registered and bounced
                  callers to Overview via the SPA index fallback. */}
              <Button variant="ghost" size="sm" onClick={() => navigate(`/workflow-audit?policyId=${selected.id}`)}>Open full audit →</Button>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G441 — PolicySkillWizard's intrinsic render is a plain inline div
          (PolicySkillWizard.jsx:713, no fixed/overlay chrome of its own).
          Without a wrapping overlay, mounting it via {showWizard && ...}
          appended the entire 5-stage wizard *below* the inventory rows,
          where the user could never see it. Hosting it inside a wide V2
          Slideout gives it the proper overlay chrome (backdrop, slide
          animation, close button) while preserving every internal step,
          API call, and the framework-binding handoff. The wizard hides
          its redundant inline Back/title header when embeddedInSlideout
          is set. The wasm_skill flow lives on a different surface
          (SkillCreateSlideout, components/v2/skill/) and is untouched. */}
      <Slideout
        isOpen={showWizard}
        onClose={closeWizard}
        width={960}
        ariaLabel={editingConfig ? 'Edit policy skill' : 'New policy skill'}
        header={(
          <div>
            {wizardScopeAgentId && (
              <button type="button" onClick={backToAgentBuild}
                className="v2-focus-ring inline-flex items-center gap-1 text-[12px] font-semibold text-v2-action hover:text-v2-action-strong mb-2">
                <ChevronRight size={13} style={{ transform: 'rotate(180deg)' }} />
                Back to {wizardBackAgentName || 'agent'} — Build phase
              </button>
            )}
            <div className="flex items-center gap-2 mb-2">
              <Pill tone="action" size="sm">5-stage pipeline</Pill>
              <Pill tone="muted" size="sm">Embed · Extract · Review · Bind · Activate</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">
              {editingConfig ? 'Edit Policy Skill' : 'New Policy Skill'}
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1 m-0">
              Compile a policy from documents, pattern templates, and/or framework controls into deterministic enforcement rules.
            </p>
          </div>
        )}
      >
        {showWizard && (
          /* G208 — Correct prop names (onCancel / onComplete, not onClose /
           * onSuccess). Pass `config` to enable in-place edit; omit for create. */
          <PolicySkillWizard
            embeddedInSlideout
            config={editingConfig || undefined}
            initialStage={wizardInitialStage}
            scopeAgentId={wizardScopeAgentId}
            onCancel={closeWizard}
            /* The wizard now closes only from its terminal outcome screen, so by the time this
             * fires the operator has already SEEN what was written. Return them to where they
             * came from: if the wizard was opened from an agent's policy skill, that agent's
             * skill — not this list, which is not where they were. */
            onComplete={() => {
              const backTo = wizardScopeAgentId;
              const backSkill = editingConfig?.skillId || null;
              closeWizard();
              if (backTo) {
                // tab=build is required, not cosmetic: the skill editor is hosted inside the
                // Build/Promote step, so without it the panel that honours ?skill= never mounts
                // and the deep-link silently lands on whatever tab was last active.
                navigate(`/agents/${backTo}?tab=build${backSkill ? `&skill=${backSkill}` : ''}`);
                return;
              }
              reload();
            }}
          />
        )}
      </Slideout>

      {/* Step 4 (Platform-Eng workflow) — Rego authoring slideout.
          Mounted alongside the legacy wizard slideout above; the two
          are independent (different state, different save paths) so
          the wizard's 5-stage pipeline path stays exactly as it was.
          Zero regression on the existing flow. */}
      <RegoEditorSlideout
        isOpen={regoSlideoutOpen}
        onClose={closeRegoSlideout}
        onSaved={onRegoSaved}
        existingRule={regoEditTarget}
      />
    </div>
  );
}

/* G438 — Add Framework slideout (was a tabbed Modal pre-G438).
 *
 * Why slideout, not modal: the V2 pattern (G378/G379) consistently uses
 * Slideout for Create flows so the user keeps the inventory visible behind
 * the form. Modals here were a leftover from earlier waves.
 *
 * Why no "Import bundled" tab: pre-G438 the slideout offered a hardcoded
 * 5-entry catalog (NIST_800_53 / SOC2 / HIPAA / ISO_27001 / PCI_DSS), but
 * the backend POST /api/v1/compliance-center/frameworks/import endpoint
 * (ComplianceCenterV2Controller#importFramework) doesn't actually maintain
 * a separate bundled catalog — it just calls createCustomFramework with
 * whatever frameworkCode comes in. Tenants already get the full seeded
 * library (34 frameworks: MITRE ATT&CK, NIST CSF 2.0, NIST AI RMF, EU AI
 * Act, HIPAA, GDPR, …) via SQL migration. The bundled tab was UI fiction
 * showing a stale subset and confused users into thinking they were
 * "importing" something special. The clean fix: just create custom
 * frameworks here. If a tenant ever clears the seed and wants the bundle
 * back, that's a platform-admin DB op, not a per-tenant UI flow.
 */
function AddFrameworkSlideout({ isOpen, onClose, onCreated }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({ name: '', sector: 'GENERAL', description: '', version: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setForm({ name: '', sector: 'GENERAL', description: '', version: '' });
  }, [isOpen]);

  const handleSubmit = async () => {
    setBusy(true); setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        sector: form.sector,
        description: form.description.trim() || null,
        version: form.version.trim() || null,
      };
      if (!payload.name) { setError('Name is required.'); setBusy(false); return; }
      const res = await api.ccCreateFramework?.(payload);
      if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Create failed');
      addToast({ tone: 'success', title: 'Framework created', description: payload.name });
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create framework');
      addToast({ tone: 'danger', title: 'Add framework failed', description: err?.message });
    } finally { setBusy(false); }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      title={<div className="flex items-center gap-2"><BookOpen size={16} className="text-v2-trust-strong" />Add Framework</div>}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={busy} iconLeft={<CheckCircle size={13} />}>
            {busy ? 'Creating…' : 'Create framework'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-v2-sm bg-v2-danger-soft border border-v2-danger-border px-3 py-2 text-[12px] text-v2-danger">
            {error}
          </div>
        )}

        <div className="rounded-v2-md bg-v2-trust-soft border border-v2-trust-border/30 px-3 py-2.5 text-[12px] text-v2-ink-secondary leading-[1.55]">
          <span className="font-semibold text-v2-ink">Custom framework.</span>{' '}
          Defines a new framework row in this org. Controls are added
          afterwards via the per-framework slideout. The bundled compliance
          library (NIST, HIPAA, SOC&nbsp;2, MITRE ATT&amp;CK, NIST CSF 2.0,
          EU AI Act, &hellip;) is seeded automatically at tenant onboarding
          and already appears in the Policy Library list.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Name *</span>
            <input type="text" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. ACME Internal Security Baseline"
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
          </label>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Sector</span>
            <select value={form.sector} onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))}
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
              <option value="GENERAL">General</option>
              <option value="GOVERNMENT">Government</option>
              <option value="HEALTHCARE">Healthcare</option>
              <option value="FINANCE">Finance</option>
              <option value="EDUCATION">Education</option>
              <option value="TECH">Technology</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Version</span>
            <input type="text" value={form.version} onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))}
              placeholder="e.g. 1.0"
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
          </label>
          <label className="block col-span-2">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Description</span>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="What this framework covers and when it applies."
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
          </label>
        </div>
      </div>
    </Slideout>
  );
}

/* ── Inventory: Policy Library (Frameworks) ─────────────────────── */
function PolicyLibraryInventory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToast } = useToast();
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  /* G376 — Add-Framework modal state for the (now-enabled) CTA. */
  const [addOpen, setAddOpen] = useState(false);
  /* G376 — Per-framework admin actions (Reimport/Archive) — track which
   * row is currently running an action so we can disable its button. */
  const [adminBusy, setAdminBusy] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.ccListFrameworks?.()?.catch(() => null);
      const list = Array.isArray(res?.data) ? res.data
                 : Array.isArray(res?.data?.frameworks) ? res.data.frameworks
                 : Array.isArray(res?.frameworks) ? res.frameworks
                 : Array.isArray(res) ? res : [];
      setFrameworks(list);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({ sector: new Set(), enabled: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ sector: new Set(), enabled: new Set() });

  const sectors = useMemo(() => {
    const s = new Set();
    for (const f of frameworks) s.add(f.sector || 'GENERAL');
    return Array.from(s).sort();
  }, [frameworks]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'sector',  label: 'Sector',  options: sectors.map(s => ({ key: s, label: s })) },
    { key: 'enabled', label: 'Status',  options: [
      { key: 'on',  label: 'Enabled' },
      { key: 'off', label: 'Disabled' },
    ]},
  ]), [sectors]);

  const filtered = useMemo(() => frameworks.filter(f => {
    if (facets.sector.size > 0 && !facets.sector.has(f.sector || 'GENERAL')) return false;
    const isOn = !!(f.enabled || f.isEnabled);
    if (facets.enabled.size > 0) {
      if (facets.enabled.has('on') && !isOn) return false;
      if (facets.enabled.has('off') && isOn) return false;
    }
    return true;
  }), [frameworks, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = frameworks.filter(f => {
          if (g.key === 'sector') return (f.sector || 'GENERAL') === opt.key;
          if (g.key === 'enabled') {
            const isOn = !!(f.enabled || f.isEnabled);
            return opt.key === 'on' ? isOn : !isOn;
          }
          return false;
        }).length;
      }
    }
    return counts;
  }, [frameworks, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const [controls, setControls] = useState([]);
  const [controlsLoading, setControlsLoading] = useState(false);

  const openSlideout = useCallback(async (row) => {
    setSelected(row); setSlideoutTab('overview'); setControls([]);
    setControlsLoading(true);
    try {
      const res = await api.ccGetFrameworkControls?.(row.id)?.catch(() => null);
      const list = Array.isArray(res?.data) ? res.data
                 : Array.isArray(res?.data?.controls) ? res.data.controls
                 : Array.isArray(res?.controls) ? res.controls
                 : Array.isArray(res) ? res : [];
      setControls(list);
    } finally { setControlsLoading(false); }
  }, []);
  const closeSlideout = () => setSelected(null);

  /* G440 — Deep-link from the Framework Coverage tab: when this inventory
   * mounts under ?framework=<id>, pre-open the slideout for that row.
   * We wait until `frameworks` is loaded so we can pass the full row
   * object (the slideout body shows name / sector / etc from it). The
   * query param is consumed once and cleared so back-navigating doesn't
   * loop. */
  useEffect(() => {
    const fid = searchParams.get('framework');
    if (!fid || !frameworks.length) return;
    const row = frameworks.find((f) => f.id === fid)
             || frameworks.find((f) => f.frameworkId === fid);
    if (row) {
      openSlideout(row);
      const next = new URLSearchParams(searchParams);
      next.delete('framework');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, frameworks, openSlideout, setSearchParams]);

  const kpis = useMemo(() => ({
    total: frameworks.length,
    active: frameworks.filter(f => f.enabled || f.isEnabled).length,
    controls: frameworks.reduce((s, f) => s + (f.controlCount || 0), 0),
    sectors: sectors.length,
  }), [frameworks, sectors]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total frameworks" value={kpis.total} tone="cyan" />
        <MetricCard label="Active" value={kpis.active} tone="success" />
        <MetricCard label="Total controls" value={kpis.controls.toLocaleString()} tone="trust" />
        <MetricCard label="Sectors" value={kpis.sectors} tone="action" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G376/G438 — Opens AddFrameworkSlideout for authoring a custom
            framework. The bundled compliance library is seeded at platform
            install (see PolicyConfigPageV2.jsx G438 comment block). */}
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setAddOpen(true)}>
          Add Framework
        </Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Framework filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState icon={<BookOpen size={20} />} title="No frameworks loaded"
            description="Import a compliance framework (NIST, SOC 2, HIPAA, ISO 27001, PCI DSS) or define a custom one."
            primaryAction={<Button variant="primary" iconLeft={<Plus />} onClick={() => setAddOpen(true)}>
              Add Framework
            </Button>} />
        ) : (
          <Table
            ariaLabel="Compliance frameworks"
            density="comfortable"
            columns={[
              { key: 'name', header: 'Framework', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{(r.description || '').slice(0, 60)}</div>
                </div>
              )},
              { key: 'sector', header: 'Sector', render: (r) => (
                <Pill tone="muted" size="sm">{r.sector || 'GENERAL'}</Pill>
              )},
              { key: 'controls', header: 'Controls', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[12px] text-v2-ink">{(r.controlCount || 0).toLocaleString()}</span>
              )},
              { key: 'enabled', header: 'Status', render: (r) => (
                <Pill tone={(r.enabled || r.isEnabled) ? 'success' : 'muted'} size="sm" dot>
                  {(r.enabled || r.isEnabled) ? 'enabled' : 'disabled'}
                </Pill>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="frameworks" />
      </div>

      <Slideout
        isOpen={!!selected} onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} framework detail` : 'Framework detail'}
        width={520}
        tabs={[
          { id: 'overview', label: 'Overview' },
          { id: 'controls', label: 'Controls', badge: controls.length || undefined },
          { id: 'admin',    label: 'Manage' },
        ]}
        activeTabId={slideoutTab} onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={(selected.enabled || selected.isEnabled) ? 'success' : 'muted'} size="sm" dot>
                {(selected.enabled || selected.isEnabled) ? 'enabled' : 'disabled'}
              </Pill>
              <Pill tone="muted" size="sm">{selected.sector || 'GENERAL'}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description?.slice(0, 100) || '—'}</p>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <Card padding="sm">
            <Card.Header title="Framework metadata" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Type</dt><dd className="text-v2-ink mt-0.5">{selected.type || selected.sector || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Version</dt><dd className="text-v2-ink mt-0.5">{selected.version || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Controls</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{(selected.controlCount || 0).toLocaleString()}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Auto-generate</dt><dd className="text-v2-ink mt-0.5">{selected.autoGenerate ? 'yes' : 'no'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'controls' && (
          <Card padding="sm">
            <Card.Header title={`${controls.length} control(s)`} />
            <Card.Body>
              {controlsLoading ? (
                <p className="text-v2-ink-muted text-[12.5px]">Loading controls…</p>
              ) : controls.length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No controls imported.</p>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-auto">
                  {/* G439 — Align field names to the backend RegulatoryControl
                      entity (controlCode / controlName / controlDescription /
                      controlCategory). Pre-G439 the renderer probed
                      controlId / name / title / description — none of which
                      match the wire payload — and rendered the raw UUID as
                      a fallback. */}
                  {controls.slice(0, 200).map((ctrl, i) => {
                    const code  = ctrl.controlCode || ctrl.controlId || ctrl.code;
                    const name  = ctrl.controlName || ctrl.name || ctrl.title;
                    const desc  = ctrl.controlDescription || ctrl.description || '';
                    const cat   = ctrl.controlCategory || ctrl.category;
                    const sev   = ctrl.defaultSeverity || ctrl.severity;
                    return (
                      <div key={ctrl.id || code || i} className="border border-v2-border-subtle rounded-v2-sm p-2.5">
                        <div className="flex items-baseline justify-between gap-2 mb-0.5">
                          <div className="flex items-baseline gap-2 min-w-0">
                            {code && (
                              <code className="font-mono-v2 text-[11px] text-v2-trust-strong shrink-0">{code}</code>
                            )}
                            <span className="font-semibold text-v2-ink text-[12.5px] truncate">{name || (code ? '' : ctrl.id)}</span>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {cat && <Pill tone="muted" size="sm">{cat}</Pill>}
                            {sev && <Pill tone={sev === 'BLOCK' ? 'danger' : sev === 'WARN' ? 'warning' : 'muted'} size="sm">{sev}</Pill>}
                          </div>
                        </div>
                        {desc && (
                          <div className="text-[11px] text-v2-ink-secondary leading-[1.5] line-clamp-2">{desc}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'admin' && (
          /* G376 — Real V2 admin tab: Reimport (refresh controls from
             source bundle) + Archive (soft-delete via bindings endpoint).
             Replaces the legacy=1 bounce. */
          <Card padding="sm">
            <Card.Header title="Manage framework" />
            <Card.Body>
              <div className="space-y-3 text-[12.5px]">
                <p className="text-v2-ink-secondary m-0">
                  Reimport pulls the latest control catalog from the bundled framework source.
                  Archive disables the framework — existing bindings stay in place but no new
                  ones can be created.
                </p>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<RefreshCw size={13} />}
                    disabled={adminBusy === selected.id}
                    onClick={async () => {
                      setAdminBusy(selected.id);
                      try {
                        const res = await api.ccReimportSingleFramework?.(selected.id);
                        if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Reimport failed');
                        addToast({ tone: 'success', title: `${selected.name} reimported`, description: 'Control catalog refreshed.' });
                        reload();
                      } catch (err) {
                        addToast({ tone: 'danger', title: 'Reimport failed', description: err?.message });
                      } finally { setAdminBusy(null); }
                    }}
                  >
                    Reimport from source
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!text-v2-danger hover:!bg-v2-danger-soft"
                    iconLeft={<Archive size={13} />}
                    disabled={adminBusy === selected.id}
                    onClick={async () => {
                      if (!window.confirm(`Archive framework "${selected.name}"? Existing bindings remain enforced; no new bindings will be allowed.`)) return;
                      setAdminBusy(selected.id);
                      try {
                        const res = await api.ccRemoveConfig?.(selected.id);
                        if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Archive failed');
                        addToast({ tone: 'success', title: `${selected.name} archived` });
                        closeSlideout();
                        reload();
                      } catch (err) {
                        addToast({ tone: 'danger', title: 'Archive failed', description: err?.message });
                      } finally { setAdminBusy(null); }
                    }}
                  >
                    Archive framework
                  </Button>
                </div>
                <div className="text-[10.5px] text-v2-ink-muted pt-2 border-t border-v2-border-subtle">
                  Reimport is safe — existing controls are matched by ID and updated in place.
                  New controls are added; controls removed upstream remain in the catalog (mark them
                  archived manually if needed).
                </div>
              </div>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G376 — Add-Framework modal (#2). */}
      <AddFrameworkSlideout isOpen={addOpen} onClose={() => setAddOpen(false)} onCreated={reload} />
    </div>
  );
}

/* G376 — Compliance levels exposed by the backend. Display-only metadata
 * for the Assign-Control modal; backend enforces the actual enum. */
const COMPLIANCE_LEVELS = [
  { value: 'REQUIRED',    label: 'Required',    desc: 'Must be fully compliant — violations block deployment.' },
  { value: 'RECOMMENDED', label: 'Recommended', desc: 'Should be compliant — violations generate warnings.' },
  { value: 'OPTIONAL',    label: 'Optional',    desc: 'Nice to have — tracked for reporting only.' },
];

/* G376 — Assign-Control modal. Binds a framework to a scope
 * (Org/Tenant/Agent) at a chosen compliance level via
 * POST /api/v1/compliance-center/config. Replaces a legacy=1 bounce. */
function AssignControlModal({ isOpen, onClose, onAssigned, frameworks }) {
  const { addToast } = useToast();
  const [frameworkId, setFrameworkId] = useState('');
  const [scope, setScope] = useState('org'); /* org | tenant | agent */
  const [level, setLevel] = useState('REQUIRED');
  const [scopeValue, setScopeValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setFrameworkId((frameworks?.[0]?.id) || '');
    setScope('org'); setLevel('REQUIRED'); setScopeValue('');
  }, [isOpen, frameworks]);

  const canSubmit = frameworkId && !busy;

  const handleSubmit = async () => {
    setBusy(true); setError(null);
    try {
      const payload = { frameworkId, complianceLevel: level };
      /* Only one scope key is set — org/tenant/agent are mutually exclusive
       * by backend convention. If the operator leaves the scope value blank
       * the binding applies at the current session's scope (handled by the
       * backend's SecurityContext resolution). */
      if (scope === 'org' && scopeValue.trim())    payload.orgId    = scopeValue.trim();
      if (scope === 'tenant' && scopeValue.trim()) payload.tenantId = scopeValue.trim();
      if (scope === 'agent' && scopeValue.trim())  payload.agentId  = scopeValue.trim();
      const res = await api.ccAddFrameworkRequirement?.(payload);
      if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Assignment failed');
      const fw = frameworks.find(f => f.id === frameworkId);
      addToast({ tone: 'success', title: `${fw?.name || 'Framework'} assigned`, description: `Scope: ${scope}${scopeValue ? ` (${scopeValue})` : ''} · Level: ${level}` });
      onAssigned?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Assignment failed');
      addToast({ tone: 'danger', title: 'Assign control failed', description: err?.message });
    } finally { setBusy(false); }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={busy ? undefined : onClose}
      width={620}
      ariaLabel="Assign control"
      header={
        <div className="flex items-center gap-2">
          <Target size={16} className="text-v2-action-strong" />
          <h2 className="text-[15px] font-bold m-0">Assign control</h2>
        </div>
      }
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit} iconLeft={<CheckCircle size={13} />}>
            {busy ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-v2-sm bg-v2-danger-soft border border-v2-danger-border px-3 py-2 text-[12px] text-v2-danger">
            {error}
          </div>
        )}
        {frameworks?.length === 0 && (
          <div className="rounded-v2-sm bg-v2-warning-soft border border-v2-warning-border px-3 py-2 text-[12px] text-v2-warning-strong">
            No frameworks available — import one from the Frameworks tab first.
          </div>
        )}

        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Framework *</span>
          <select value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}
            className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
            {frameworks.map(f => (
              <option key={f.id} value={f.id}>{f.name || f.id}{f.controlCount ? ` (${f.controlCount} controls)` : ''}</option>
            ))}
          </select>
        </label>

        <div>
          <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Compliance level *</span>
          <ul className="space-y-1.5 m-0 p-0 list-none">
            {COMPLIANCE_LEVELS.map((cl) => (
              <li key={cl.value}>
                <label className={'flex items-start gap-3 rounded-v2-md border p-2.5 cursor-pointer transition-colors ' +
                  (level === cl.value ? 'border-v2-action bg-v2-action-soft' : 'border-v2-border-subtle hover:bg-v2-muted/30')}>
                  <input type="radio" name="cl" checked={level === cl.value} onChange={() => setLevel(cl.value)} className="mt-0.5" />
                  <div className="flex-1">
                    <strong className="text-v2-ink text-[12.5px]">{cl.label}</strong>
                    <div className="text-[11px] text-v2-ink-muted mt-0.5">{cl.desc}</div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Scope</span>
            <select value={scope} onChange={(e) => { setScope(e.target.value); setScopeValue(''); }}
              className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
              <option value="org">Organization</option>
              <option value="tenant">Tenant</option>
              <option value="agent">Agent</option>
            </select>
          </label>
          <label className="block col-span-2">
            <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">{scope === 'org' ? 'Org ID' : scope === 'tenant' ? 'Tenant ID' : 'Agent ID'} <span className="text-v2-ink-muted normal-case">(optional)</span></span>
            <input type="text" value={scopeValue} onChange={(e) => setScopeValue(e.target.value)}
              placeholder={`Leave blank to use current ${scope}`}
              className="v2-focus-ring w-full text-[12.5px] font-mono-v2 border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
          </label>
        </div>
      </div>
    </Slideout>
  );
}

/* ── Inventory: Control Config (effective compliance) ───────────── */
function ControlConfigInventory() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [effective, setEffective] = useState([]);
  const [loading, setLoading] = useState(true);
  /* G376 — Assign-control modal state + framework list for picker. */
  const [assignOpen, setAssignOpen] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [removing, setRemoving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [eff, fw] = await Promise.all([
        api.ccGetEffectiveCompliance?.({})?.catch(() => null),
        api.ccListFrameworks?.()?.catch(() => null),
      ]);
      const list = Array.isArray(eff?.data?.controls) ? eff.data.controls
                 : Array.isArray(eff?.data) ? eff.data
                 : Array.isArray(eff?.controls) ? eff.controls
                 : Array.isArray(eff) ? eff : [];
      setEffective(list);
      const fwList = Array.isArray(fw?.data) ? fw.data
                   : Array.isArray(fw?.data?.frameworks) ? fw.data.frameworks
                   : Array.isArray(fw?.frameworks) ? fw.frameworks
                   : Array.isArray(fw) ? fw : [];
      setFrameworks(fwList);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({ scope: new Set(), severity: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ scope: new Set(), severity: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'scope', label: 'Scope', options: [
      { key: 'org',    label: 'Organization' },
      { key: 'tenant', label: 'Tenant' },
      { key: 'agent',  label: 'Agent' },
    ]},
    { key: 'severity', label: 'Severity', options: [
      { key: 'critical', label: 'Critical' },
      { key: 'high',     label: 'High' },
      { key: 'medium',   label: 'Medium' },
      { key: 'low',      label: 'Low' },
    ]},
  ]), []);

  const filtered = useMemo(() => effective.filter(c => {
    if (facets.scope.size > 0 && !facets.scope.has((c.scope || 'org').toLowerCase())) return false;
    if (facets.severity.size > 0 && !facets.severity.has((c.severity || 'medium').toLowerCase())) return false;
    return true;
  }), [effective, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = effective.filter(c => {
          if (g.key === 'scope') return (c.scope || 'org').toLowerCase() === opt.key;
          if (g.key === 'severity') return (c.severity || 'medium').toLowerCase() === opt.key;
          return false;
        }).length;
      }
    }
    return counts;
  }, [effective, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const kpis = useMemo(() => ({
    total: effective.length,
    org: effective.filter(c => (c.scope || 'org') === 'org').length,
    tenant: effective.filter(c => c.scope === 'tenant').length,
    agent: effective.filter(c => c.scope === 'agent').length,
  }), [effective]);

  const [selected, setSelected] = useState(null);
  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total controls" value={kpis.total} tone="cyan" />
        <MetricCard label="Org-scoped"     value={kpis.org} tone="trust" />
        <MetricCard label="Tenant-scoped"  value={kpis.tenant} tone="action" />
        <MetricCard label="Agent-scoped"   value={kpis.agent} tone="success" />
      </div>

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G376 — Real V2 build: opens AssignControlModal (framework × scope
            × compliance level). Replaces the legacy=1 bounce. */}
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setAssignOpen(true)}>
          Assign control
        </Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Control filters" />
        {filtered.length === 0 && !loading ? (
          <EmptyState icon={<Target size={20} />} title="No controls assigned"
            description="Bind framework controls to your organization, tenant, or specific agents to begin enforcement."
            primaryAction={
              <Button variant="primary" iconLeft={<Plus />} onClick={() => setAssignOpen(true)}
                disabled={frameworks.length === 0}
                title={frameworks.length === 0 ? 'Import a framework on the Frameworks tab first.' : undefined}>
                Assign control
              </Button>
            } />
        ) : (
          <Table
            ariaLabel="Effective controls"
            density="compact"
            columns={[
              { key: 'control', header: 'Control', render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink text-[12.5px]">{r.controlId || r.name || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{(r.title || r.description || '').slice(0, 60)}</div>
                </div>
              )},
              { key: 'framework', header: 'Framework', render: (r) => (
                <Pill tone="cyan" size="sm">{r.frameworkName || r.framework || '—'}</Pill>
              )},
              { key: 'scope', header: 'Scope', render: (r) => (
                <Pill tone="muted" size="sm">{r.scope || 'org'}</Pill>
              )},
              { key: 'severity', header: 'Severity', render: (r) => (
                <Pill tone={r.severity === 'critical' ? 'danger' : r.severity === 'high' ? 'warning' : 'muted'} size="sm">
                  {r.severity || 'medium'}
                </Pill>
              )},
              { key: 'enforcement', header: 'Enforcement', render: (r) => (
                <Pill tone={r.enforced ? 'success' : 'warning'} size="sm" dot>
                  {r.enforced ? 'enforced' : 'unmapped'}
                </Pill>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id || r.controlId}
            onRowClick={setSelected}
          />
        )}
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="controls" />
      </div>

      <Slideout isOpen={!!selected} onClose={() => setSelected(null)}
        ariaLabel="Control detail" width={520}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone={selected.enforced ? 'success' : 'warning'} size="sm" dot>
                {selected.enforced ? 'enforced' : 'unmapped'}
              </Pill>
              <Pill tone="muted" size="sm">{selected.scope || 'org'}</Pill>
              <Pill tone={selected.severity === 'critical' ? 'danger' : selected.severity === 'high' ? 'warning' : 'muted'} size="sm">
                {selected.severity || 'medium'}
              </Pill>
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0">{selected.controlId || selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">{selected.frameworkName || selected.framework || '—'}</p>
          </div>
        )}
      >
        {selected && (
          /* G376 — Replaces a JSON-dump slideout with a structured detail
             view + per-control Remove. Backend cascades through ComplianceConfig
             → FrameworkBinding → PolicyRules → PolicyBindings. */
          <>
            <Card padding="sm" className="mb-3">
              <Card.Header title="Control detail" />
              <Card.Body>
                <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Control ID</dt>
                    <dd className="text-v2-ink mt-0.5 font-mono-v2 break-all">{selected.controlId || '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Framework</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.frameworkName || selected.framework || '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Scope</dt>
                    <dd className="text-v2-ink mt-0.5 capitalize">{selected.scope || 'org'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Compliance level</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.complianceLevel || '—'}</dd></div>
                  <div className="col-span-2">
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Title</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.title || selected.name || '—'}</dd>
                  </div>
                  {selected.description && (
                    <div className="col-span-2">
                      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Description</dt>
                      <dd className="text-v2-ink-secondary mt-0.5">{selected.description}</dd>
                    </div>
                  )}
                </dl>
              </Card.Body>
            </Card>

            <Card padding="sm">
              <Card.Header title="Manage" />
              <Card.Body>
                <p className="text-[12px] text-v2-ink-secondary mb-3 m-0">
                  To change scope or compliance level, remove this binding and create a new one.
                  Re-assignment uses the Assign control button on the inventory.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="!text-v2-danger hover:!bg-v2-danger-soft"
                  iconLeft={<Trash2 size={13} />}
                  disabled={removing}
                  onClick={async () => {
                    const removeId = selected.configId || selected._bindingId || selected.id;
                    if (!removeId) {
                      addToast({ tone: 'danger', title: 'Cannot remove', description: 'No binding ID available on this row.' });
                      return;
                    }
                    if (!window.confirm(`Remove control "${selected.controlId || selected.name}"? Cascades through ComplianceConfig → FrameworkBinding → PolicyRules.`)) return;
                    setRemoving(true);
                    try {
                      const res = await api.ccRemoveConfig?.(removeId);
                      if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Remove failed');
                      addToast({ tone: 'success', title: 'Control removed' });
                      setSelected(null);
                      reload();
                    } catch (err) {
                      addToast({ tone: 'danger', title: 'Remove failed', description: err?.message });
                    } finally { setRemoving(false); }
                  }}
                >
                  {removing ? 'Removing…' : 'Remove control'}
                </Button>
              </Card.Body>
            </Card>
          </>
        )}
      </Slideout>

      {/* G376 — Assign-Control modal (#4). */}
      <AssignControlModal isOpen={assignOpen} onClose={() => setAssignOpen(false)} onAssigned={reload} frameworks={frameworks} />
    </div>
  );
}

/* ── Dashboard: Framework Coverage (gap analysis) ───────────────── */
function FrameworkCoverageDashboard() {
  const navigate = useNavigate();
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.ccGetCoverage?.({})?.catch(() => null);
      setCoverage(res?.data || res || null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const rows = useMemo(() => {
    if (!coverage) return [];
    if (Array.isArray(coverage.frameworks)) return coverage.frameworks;
    if (Array.isArray(coverage.coverage)) return coverage.coverage;
    if (Array.isArray(coverage)) return coverage;
    return [];
  }, [coverage]);

  const kpis = useMemo(() => {
    const totalFrameworks = rows.length;
    const totalControls = rows.reduce((s, r) => s + (r.totalControls || r.totalCount || 0), 0);
    const enforcedControls = rows.reduce((s, r) => s + (r.enforcedControls || r.enforcedCount || 0), 0);
    const gaps = totalControls - enforcedControls;
    const pct = totalControls > 0 ? Math.round((enforcedControls / totalControls) * 100) : 0;
    return { totalFrameworks, totalControls, enforcedControls, gaps, pct };
  }, [rows]);

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(rows);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
        <MetricCard label="Frameworks"        value={kpis.totalFrameworks} tone="cyan" />
        <MetricCard label="Total controls"    value={kpis.totalControls.toLocaleString()} tone="trust" />
        <MetricCard label="Enforced"          value={kpis.enforcedControls.toLocaleString()} tone="success" />
        <MetricCard label="Coverage gaps"     value={kpis.gaps.toLocaleString()} tone={kpis.gaps > 0 ? 'warning' : 'muted'} />
        <MetricCard label="Coverage %"        value={`${kpis.pct}%`} tone={kpis.pct >= 80 ? 'success' : kpis.pct >= 50 ? 'warning' : 'danger'} />
      </div>

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G376 — The per-control bridge-gap authoring workspace (V1's heaviest
            panel — ~1700 LOC) is still a Large item; the read-side coverage
            matrix is already rendered inline below. For the bridge-gap flow,
            operators jump back to the Frameworks tab to import missing
            frameworks and the Policies tab to author per-control rules.
            G440 — Both tab ids corrected: 'frameworks' → 'policy-library'
            and 'policies' → 'configurations' to match the TABS array. The
            old strings silently no-op'd (URL changed, tab unchanged). */}
        <Button variant="secondary" size="sm" iconLeft={<BookOpen size={13} />} onClick={() => navigate('/policy-config?tab=policy-library')}>
          Manage frameworks
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<Shield size={13} />} onClick={() => navigate('/policy-config?tab=configurations')}>
          Author policies
        </Button>
      </div>

      <Card>
        <Card.Header title={`Coverage by framework · ${rows.length}`} />
        <Card.Body>
          {loading ? (
            <p className="text-v2-ink-muted text-[12.5px]">Loading coverage…</p>
          ) : rows.length === 0 ? (
            <EmptyState icon={<Layers size={20} />} title="No coverage data"
              description="Bind controls to frameworks to compute coverage gaps." />
          ) : (
            <Table
              ariaLabel="Framework coverage"
              density="compact"
              onRowClick={(r) => {
                /* G440 — Drill-in: jump to the Policy Library tab and
                 * deep-link the slideout open on this framework. The
                 * row id may live under several keys depending on the
                 * coverage endpoint's response shape, so we probe in
                 * order. PolicyLibraryInventory reads ?framework=<id>
                 * on tab activation. */
                const fid = r.id || r.frameworkId || r.framework_id;
                if (fid) navigate(`/policy-config?tab=policy-library&framework=${encodeURIComponent(fid)}`);
                else navigate('/policy-config?tab=policy-library');
              }}
              columns={[
                { key: 'framework', header: 'Framework', render: (r) => (
                  <span className="font-semibold text-v2-ink text-[12.5px] hover:text-v2-action-strong cursor-pointer">
                    {r.frameworkName || r.name || r.id}
                  </span>
                )},
                { key: 'total', header: 'Total controls', align: 'right', render: (r) => (
                  <span className="font-mono-v2 text-[12px]">{(r.totalControls || r.totalCount || 0).toLocaleString()}</span>
                )},
                { key: 'enforced', header: 'Enforced', align: 'right', render: (r) => (
                  <span className="font-mono-v2 text-[12px] text-v2-success">{(r.enforcedControls || r.enforcedCount || 0).toLocaleString()}</span>
                )},
                { key: 'gaps', header: 'Gaps', align: 'right', render: (r) => {
                  const gap = (r.totalControls || r.totalCount || 0) - (r.enforcedControls || r.enforcedCount || 0);
                  return <span className={`font-mono-v2 text-[12px] ${gap > 0 ? 'text-v2-danger' : 'text-v2-ink-muted'}`}>{gap.toLocaleString()}</span>;
                }},
                { key: 'pct', header: 'Coverage', align: 'right', render: (r) => {
                  const total = r.totalControls || r.totalCount || 0;
                  const enf = r.enforcedControls || r.enforcedCount || 0;
                  const pct = total > 0 ? Math.round((enf / total) * 100) : 0;
                  return (
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-20 h-1.5 bg-v2-muted rounded-full overflow-hidden">
                        <div className={`h-full ${pct >= 80 ? 'bg-v2-success' : pct >= 50 ? 'bg-v2-warning' : 'bg-v2-danger'}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-mono-v2 text-[11px] text-v2-ink-secondary w-9 text-right">{pct}%</span>
                    </div>
                  );
                }},
              ]}
              rows={visibleRows}
              rowKey={(r) => r.id || r.frameworkName || r.name}
            />
          )}
          <Pagination totalItems={rows.length} page={pg.page} pageSize={pg.pageSize}
            onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="frameworks" />
        </Card.Body>
      </Card>
    </div>
  );
}

/* ── Inventory: Control Drift (alerts) ──────────────────────────── */
function ControlDriftInventory() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [alerts, setAlerts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  /* G376 — Triage state. The slideout's three buttons drive transitions:
   *   open → acknowledged → resolved
   *   open → suppressed (terminal until reopened by detection)
   * Each transition records a reason on the alert via ccUpdateDriftAlert.
   * A read-only history of the transitions could be added later. */
  const [triageReason, setTriageReason] = useState('');
  const [triageBusy, setTriageBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, dRes] = await Promise.all([
        api.ccGetDriftAlerts?.({})?.catch(() => null),
        api.ccGetDriftDashboard?.()?.catch(() => null),
      ]);
      const list = Array.isArray(aRes?.data?.alerts) ? aRes.data.alerts
                 : Array.isArray(aRes?.data) ? aRes.data
                 : Array.isArray(aRes?.alerts) ? aRes.alerts
                 : Array.isArray(aRes) ? aRes : [];
      setAlerts(list);
      setDashboard(dRes?.data || dRes || null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const [facets, setFacets] = useState(() => ({ status: new Set(), severity: new Set() }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    c ? n.add(oKey) : n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ status: new Set(), severity: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status', label: 'Status', options: [
      { key: 'open',         label: 'Open' },
      { key: 'acknowledged', label: 'Acknowledged' },
      { key: 'resolved',     label: 'Resolved' },
      { key: 'suppressed',   label: 'Suppressed' },
    ]},
    { key: 'severity', label: 'Severity', options: [
      { key: 'critical', label: 'Critical' },
      { key: 'high',     label: 'High' },
      { key: 'medium',   label: 'Medium' },
      { key: 'low',      label: 'Low' },
    ]},
  ]), []);

  const filtered = useMemo(() => alerts.filter(a => {
    if (facets.status.size > 0 && !facets.status.has((a.status || 'open').toLowerCase())) return false;
    if (facets.severity.size > 0 && !facets.severity.has((a.severity || 'medium').toLowerCase())) return false;
    return true;
  }), [alerts, facets]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = alerts.filter(a => {
          if (g.key === 'status') return (a.status || 'open').toLowerCase() === opt.key;
          if (g.key === 'severity') return (a.severity || 'medium').toLowerCase() === opt.key;
          return false;
        }).length;
      }
    }
    return counts;
  }, [alerts, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const kpis = useMemo(() => ({
    open:    alerts.filter(a => (a.status || 'open') === 'open').length,
    critical:alerts.filter(a => a.severity === 'critical').length,
    resolved:alerts.filter(a => a.status === 'resolved').length,
    total:   alerts.length,
  }), [alerts]);

  const [selected, setSelected] = useState(null);
  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Open alerts"  value={kpis.open} tone={kpis.open > 0 ? 'danger' : 'muted'} />
        <MetricCard label="Critical"     value={kpis.critical} tone={kpis.critical > 0 ? 'danger' : 'muted'} />
        <MetricCard label="Resolved"     value={kpis.resolved} tone="success" />
        <MetricCard label="Total alerts" value={kpis.total} tone="cyan" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G376 — The full V2 drift workspace (exception windows CRUD,
            timeline view, etc.) is still a Large item; meanwhile the
            inline triage IS the workspace for the open-alert flow.
            Button now anchors to the alerts table for parity with the
            old "open workspace" affordance. */}
        <Button variant="secondary" size="sm" iconLeft={<TrendingUp size={13} />}
          onClick={() => { /* Scroll into the alerts list. */
            const el = document.querySelector('[aria-label="Drift alerts"]');
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}>
          Jump to alerts
        </Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Drift filters" />
        {filtered.length === 0 && !loading ? (
          <EmptyState icon={<TrendingUp size={20} />} title={alerts.length === 0 ? 'No drift detected' : 'No alerts match filters'}
            description={alerts.length === 0
              ? 'Control assignments are within their approved baselines.'
              : 'Adjust filters or clear them to see more alerts.'} />
        ) : (
          <Table
            ariaLabel="Drift alerts"
            density="compact"
            columns={[
              { key: 'control', header: 'Control / Scope', render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink text-[12.5px]">{r.controlId || r.controlName || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{r.scope || r.targetType || '—'}: {r.targetId || r.scopeId || '—'}</div>
                </div>
              )},
              { key: 'severity', header: 'Severity', render: (r) => (
                <Pill tone={r.severity === 'critical' ? 'danger' : r.severity === 'high' ? 'warning' : 'muted'} size="sm">
                  {r.severity || 'medium'}
                </Pill>
              )},
              { key: 'reason', header: 'Drift reason', render: (r) => (
                <span className="text-[12px] text-v2-ink-secondary">{(r.reason || r.description || '').slice(0, 60)}</span>
              )},
              { key: 'status', header: 'Status', render: (r) => (
                <Pill tone={r.status === 'resolved' ? 'success' : r.status === 'acknowledged' ? 'warning' : r.status === 'suppressed' ? 'muted' : 'danger'} size="sm" dot>
                  {r.status || 'open'}
                </Pill>
              )},
              { key: 'detected', header: 'Detected', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.detectedAt || r.createdAt ? new Date(r.detectedAt || r.createdAt).toLocaleDateString() : '—'}</span>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={setSelected}
          />
        )}
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <div />
        <Pagination totalItems={filtered.length} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize} noun="alerts" />
      </div>

      <Slideout
        isOpen={!!selected}
        onClose={() => { setSelected(null); setTriageReason(''); }}
        ariaLabel="Drift alert triage"
        width={560}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Pill tone={selected.status === 'resolved' ? 'success' : selected.status === 'acknowledged' ? 'warning' : selected.status === 'suppressed' ? 'muted' : 'danger'} size="sm" dot>
                {selected.status || 'open'}
              </Pill>
              <Pill tone={selected.severity === 'critical' ? 'danger' : selected.severity === 'high' ? 'warning' : 'muted'} size="sm">
                {selected.severity || 'medium'}
              </Pill>
            </div>
            <h2 className="text-[16px] font-bold tracking-tight m-0">{selected.controlId || selected.controlName || 'Drift alert'}</h2>
            <p className="text-[11.5px] text-v2-ink-secondary mt-1 font-mono-v2">{(selected.id || '').slice(0, 16)}…</p>
          </div>
        )}
      >
        {selected && (
          /* G376 — Replaces JSON dump with structured detail + triage
             actions. Each transition writes a reason on the alert. */
          <>
            <Card padding="sm" className="mb-3">
              <Card.Header title="Alert detail" />
              <Card.Body>
                <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Control</dt>
                    <dd className="text-v2-ink mt-0.5 font-mono-v2 break-all">{selected.controlId || '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Detected</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.detectedAt || selected.createdAt ? new Date(selected.detectedAt || selected.createdAt).toLocaleString() : '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Scope</dt>
                    <dd className="text-v2-ink mt-0.5 capitalize">{selected.scope || selected.targetType || '—'}: {selected.scopeId || selected.targetId || '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Severity</dt>
                    <dd className="text-v2-ink mt-0.5 capitalize">{selected.severity || 'medium'}</dd></div>
                  {(selected.reason || selected.description) && (
                    <div className="col-span-2">
                      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Drift reason</dt>
                      <dd className="text-v2-ink-secondary mt-0.5">{selected.reason || selected.description}</dd>
                    </div>
                  )}
                </dl>
              </Card.Body>
            </Card>

            <Card padding="sm">
              <Card.Header title="Triage" />
              <Card.Body>
                <p className="text-[12px] text-v2-ink-secondary mb-2 m-0">
                  Pick a transition. The reason is required for Resolve / Suppress and recommended
                  for Acknowledge — it gets written into the alert audit history.
                </p>
                <label className="block mb-3">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Reason</span>
                  <textarea
                    value={triageReason}
                    onChange={(e) => setTriageReason(e.target.value)}
                    rows={3}
                    placeholder="Why are you transitioning this alert? What investigation occurred?"
                    className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface"
                  />
                </label>

                <div className="flex flex-wrap items-center gap-2">
                  {(['acknowledge', 'resolve', 'suppress']).map((action) => {
                    const targetStatus = action === 'acknowledge' ? 'acknowledged'
                                       : action === 'resolve' ? 'resolved'
                                       : 'suppressed';
                    const isCurrent = (selected.status || 'open') === targetStatus;
                    const needsReason = action !== 'acknowledge';
                    const label = action.charAt(0).toUpperCase() + action.slice(1);
                    return (
                      <Button
                        key={action}
                        variant={action === 'resolve' ? 'primary' : action === 'suppress' ? 'ghost' : 'secondary'}
                        size="sm"
                        disabled={triageBusy || isCurrent || (needsReason && !triageReason.trim())}
                        title={isCurrent ? `Already ${targetStatus}` : (needsReason && !triageReason.trim() ? 'Reason required' : undefined)}
                        onClick={async () => {
                          setTriageBusy(true);
                          try {
                            const res = await api.ccUpdateDriftAlert?.(selected.id, {
                              status: targetStatus,
                              reason: triageReason.trim() || null,
                            });
                            if (res && res.ok === false) throw new Error(res.error?.message || res.error || `${label} failed`);
                            addToast({ tone: 'success', title: `Alert ${targetStatus}` });
                            setSelected(null);
                            setTriageReason('');
                            reload();
                          } catch (err) {
                            addToast({ tone: 'danger', title: `${label} failed`, description: err?.message });
                          } finally { setTriageBusy(false); }
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </>
        )}
      </Slideout>
    </div>
  );
}

/* ── Dashboard: Control Scorecard (compliance posture) ──────────── */
function ControlScorecardDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [eligible, setEligible] = useState([]);
  /* G439 — agentId → display-name map. The compliance endpoints return
   * raw AgentComplianceScore rows whose only identifier is agentId (no
   * name field); pre-G439 the table rendered the bare UUID. We hydrate
   * once at load time by joining against api.getAgents(). */
  const [agentNames, setAgentNames] = useState(() => new Map());
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, alRes, elRes, agRes] = await Promise.all([
        api.getComplianceOverview?.()?.catch(() => null),
        api.getComplianceAlerts?.(75)?.catch(() => null),
        api.getPromotionEligible?.(90, 100)?.catch(() => null),
        api.getAgents?.()?.catch(() => null),
      ]);
      setOverview(ovRes?.data || ovRes || null);
      const aList = Array.isArray(alRes?.data?.alerts) ? alRes.data.alerts
                  : Array.isArray(alRes?.data) ? alRes.data
                  : Array.isArray(alRes) ? alRes : [];
      setAlerts(aList);
      const eList = Array.isArray(elRes?.data?.agents) ? elRes.data.agents
                  : Array.isArray(elRes?.data) ? elRes.data
                  : Array.isArray(elRes) ? elRes : [];
      setEligible(eList);
      /* G439 — Build id → name map. Accept either a flat array of Agent
       * rows or the various wrapper envelopes the backend may return. */
      const agentList = Array.isArray(agRes?.data?.agents) ? agRes.data.agents
                      : Array.isArray(agRes?.data) ? agRes.data
                      : Array.isArray(agRes) ? agRes : [];
      const next = new Map();
      for (const a of agentList) {
        if (a?.id && (a.name || a.displayName)) next.set(a.id, a.name || a.displayName);
      }
      setAgentNames(next);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  /* G439 — Resolve the best display string for an agent row. Prefers
   * the hydrated name; falls back to inline fields (in case the backend
   * starts joining server-side); last resort is the UUID. */
  const agentLabel = useCallback((row) => {
    const id = row?.agentId || row?.id;
    return (id && agentNames.get(id))
        || row?.agentName
        || row?.name
        || row?.displayName
        || id
        || '—';
  }, [agentNames]);

  const kpis = useMemo(() => ({
    avgScore:  overview?.averageScore ?? overview?.avgComplianceScore ?? '—',
    healthy:   overview?.healthyAgents ?? overview?.greenAgents ?? '—',
    atRisk:    overview?.atRiskAgents ?? overview?.redAgents ?? alerts.length,
    eligible:  eligible.length,
  }), [overview, alerts, eligible]);

  const pgAlerts = usePaginationState(50);
  const visibleAlerts = pgAlerts.paginate(alerts);
  const pgEligible = usePaginationState(50);
  const visibleEligible = pgEligible.paginate(eligible);

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Avg compliance score" value={kpis.avgScore !== '—' ? `${kpis.avgScore}%` : '—'}
          tone={typeof kpis.avgScore === 'number' && kpis.avgScore >= 80 ? 'success' : 'warning'} />
        <MetricCard label="Healthy agents"     value={kpis.healthy} tone="success" />
        <MetricCard label="At-risk agents"     value={kpis.atRisk} tone={kpis.atRisk > 0 ? 'danger' : 'muted'} />
        <MetricCard label="Promotion eligible" value={kpis.eligible} tone="trust" />
      </div>

      <div className="flex items-center justify-end gap-2 mb-4">
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
        {/* G376 — Replaced legacy=1 bounce. Recalc fires the backend
            pipeline-score recompute for every agent in scope (no agent ID
            arg → recompute all). The full per-agent threshold-edit /
            promotion workflow is still a Large item; for now this surfaces
            the most-requested action (refresh scores after policy edits)
            directly on the read-side view. */}
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RefreshCw size={13} />}
          onClick={async () => {
            try {
              const res = await api.ccRecalculatePipelineScore?.();
              if (res && res.ok === false) throw new Error(res.error?.message || res.error || 'Recalc failed');
              reload();
            } catch (err) {
              /* Silent fallback — backend reload is best-effort. */
              reload();
            }
          }}
        >
          Recalculate scores
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<Activity size={13} />} onClick={() => navigate('/agents')}>
          View agent inventory
        </Button>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <Card.Header title={`At-risk agents · ${alerts.length}`} />
          <Card.Body>
            <p className="text-[11px] text-v2-ink-muted mb-3">Agents below the 75% compliance threshold.</p>
            {alerts.length === 0 ? (
              <p className="text-v2-ink-muted text-[12.5px]">No at-risk agents.</p>
            ) : (
              <>
                <Table
                  ariaLabel="Compliance alerts"
                  density="compact"
                  columns={[
                    { key: 'agent', header: 'Agent', render: (r) => {
                      const id = r?.agentId || r?.id;
                      const label = agentLabel(r);
                      const isUuid = typeof label === 'string' && /^[0-9a-f]{8}-/.test(label);
                      return (
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-semibold text-v2-ink truncate" title={id || label}>
                            {isUuid ? <code className="font-mono-v2 text-[11px] text-v2-ink-muted">{String(label).slice(0, 8)}…</code> : label}
                          </div>
                          {!isUuid && id && (
                            <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">{String(id).slice(0, 8)}…</code>
                          )}
                        </div>
                      );
                    }},
                    { key: 'score', header: 'Score', align: 'right', render: (r) => (
                      <span className={`font-mono-v2 text-[12px] font-bold ${(r.complianceScore || r.score || 0) < 50 ? 'text-v2-danger' : 'text-v2-warning'}`}>
                        {r.complianceScore || r.score || 0}%
                      </span>
                    )},
                    { key: 'reason', header: 'Issue', render: (r) => (
                      <span className="text-[11.5px] text-v2-ink-secondary">{(r.reason || r.alertReason || '').slice(0, 50)}</span>
                    )},
                  ]}
                  rows={visibleAlerts}
                  rowKey={(r) => r.id || r.agentId}
                />
                <Pagination totalItems={alerts.length} page={pgAlerts.page} pageSize={pgAlerts.pageSize}
                  onPageChange={pgAlerts.setPage} onPageSizeChange={pgAlerts.setPageSize} noun="alerts" />
              </>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header title={`Promotion eligible · ${eligible.length}`} />
          <Card.Body>
            <p className="text-[11px] text-v2-ink-muted mb-3">Sustained ≥90% compliance over the evaluation window.</p>
            {eligible.length === 0 ? (
              <p className="text-v2-ink-muted text-[12.5px]">No agents currently eligible.</p>
            ) : (
              <>
                <Table
                  ariaLabel="Promotion eligible agents"
                  density="compact"
                  columns={[
                    { key: 'agent', header: 'Agent', render: (r) => {
                      const id = r?.agentId || r?.id;
                      const label = agentLabel(r);
                      const isUuid = typeof label === 'string' && /^[0-9a-f]{8}-/.test(label);
                      return (
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-semibold text-v2-ink truncate" title={id || label}>
                            {isUuid ? <code className="font-mono-v2 text-[11px] text-v2-ink-muted">{String(label).slice(0, 8)}…</code> : label}
                          </div>
                          {!isUuid && id && (
                            <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted">{String(id).slice(0, 8)}…</code>
                          )}
                        </div>
                      );
                    }},
                    { key: 'score', header: 'Score', align: 'right', render: (r) => (
                      <span className="font-mono-v2 text-[12px] text-v2-success font-bold">{r.complianceScore || r.score || 0}%</span>
                    )},
                    { key: 'level', header: 'Current level', render: (r) => (
                      <Pill tone="trust" size="sm">{r.autonomyLevel || r.recommendedAutonomy || r.currentLevel || '—'}</Pill>
                    )},
                  ]}
                  rows={visibleEligible}
                  rowKey={(r) => r.id || r.agentId}
                />
                <Pagination totalItems={eligible.length} page={pgEligible.page} pageSize={pgEligible.pageSize}
                  onPageChange={pgEligible.setPage} onPageSizeChange={pgEligible.setPageSize} noun="agents" />
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}

/* ── Inner page ─────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────
 *  G432 — Data Classification Inventory (V2)
 *
 *  Replaces the legacy DataClassificationRulesPanel with the canonical
 *  V2 surface (MetricCard + FacetRail + Table + Slideout + EmptyState +
 *  pagination) so the Data Classification tab matches Policy Library /
 *  Control Config / Framework Coverage visually.
 *
 *  No behaviour change: same API contracts (api.listDpgRules /
 *  getDpgRuleStats / toggleDpgRule / deleteDpgRule), same scope inference
 *  (SYSTEM rules can't be deleted), same compliance-framework / severity
 *  / detection-method rendering. Reuses the existing
 *  DataClassificationRuleEditor for create/edit — that editor is already
 *  its own self-contained slideout-style modal.
 *  ───────────────────────────────────────────────────────────────── */
const DPG_SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000011';
const DPG_CATEGORY_LABELS = {
  PII: 'PII',
  CREDENTIAL: 'Credential',
  FINANCIAL: 'Financial',
  HEALTH: 'Health',
  CORPORATE: 'Corporate',
  CUSTOM: 'Custom',
};
const DPG_ACTION_TONES = {
  BLOCK:    'danger',
  REDACT:   'danger',
  TOKENIZE: 'action',
  MASK:     'warning',
  LOG:      'muted',
};
const inferDpgScope = (rule) => {
  if (!rule) return 'TENANT';
  if (rule.tenantId === DPG_SYSTEM_TENANT_ID) return 'SYSTEM';
  if (rule.orgId) return 'ORG';
  return 'TENANT';
};

function DataClassificationInventory() {
  const { addToast } = useToast();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, blocking: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | rule object

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, statsRes] = await Promise.all([
        api.listDpgRules?.() ?? Promise.resolve({ ok: true, data: [] }),
        api.getDpgRuleStats?.() ?? Promise.resolve({ ok: true, data: {} }),
      ]);
      if (rulesRes?.ok) setRules(Array.isArray(rulesRes.data) ? rulesRes.data : []);
      if (statsRes?.ok) setStats(statsRes.data || {});
      if (!rulesRes?.ok) {
        addToast({ tone: 'danger', title: 'Load failed', description: rulesRes?.data?.message || 'Could not load classification rules' });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Load failed', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);
  useEffect(() => { reload(); }, [reload]);

  // ── Facet state ────────────────────────────────────────────────
  const [facets, setFacets] = useState(() => ({
    category: new Set(), action: new Set(), scope: new Set(), status: new Set(),
  }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    if (c) n.add(oKey); else n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({
    category: new Set(), action: new Set(), scope: new Set(), status: new Set(),
  });
  const [search, setSearch] = useState('');

  // ── Derived: which categories/actions/scopes are present ──────
  const presentCategories = useMemo(() => {
    const s = new Set();
    for (const r of rules) s.add(r.category || 'CUSTOM');
    return Array.from(s);
  }, [rules]);
  const presentActions = useMemo(() => {
    const s = new Set();
    for (const r of rules) if (r.action) s.add(r.action);
    return Array.from(s);
  }, [rules]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'category', label: 'Category', options: presentCategories.map(c => ({ key: c, label: DPG_CATEGORY_LABELS[c] || c })) },
    { key: 'action',   label: 'Action',   options: presentActions.map(a => ({ key: a, label: a })) },
    { key: 'scope',    label: 'Scope',    options: [
      { key: 'SYSTEM', label: 'System defaults' },
      { key: 'TENANT', label: 'Tenant-wide' },
      { key: 'ORG',    label: 'Org overrides' },
    ]},
    { key: 'status',   label: 'Status',   options: [
      { key: 'enabled',  label: 'Enabled' },
      { key: 'disabled', label: 'Disabled' },
    ]},
  ]), [presentCategories, presentActions]);

  const matchesFacets = useCallback((r) => {
    if (facets.category.size > 0 && !facets.category.has(r.category || 'CUSTOM')) return false;
    if (facets.action.size > 0   && !facets.action.has(r.action)) return false;
    if (facets.scope.size > 0    && !facets.scope.has(inferDpgScope(r))) return false;
    if (facets.status.size > 0) {
      const wantsOn  = facets.status.has('enabled');
      const wantsOff = facets.status.has('disabled');
      const isOn = !!r.enabled;
      if (wantsOn && !wantsOff && !isOn) return false;
      if (wantsOff && !wantsOn && isOn) return false;
    }
    return true;
  }, [facets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter(r => {
      if (!matchesFacets(r)) return false;
      if (q) {
        const hay = `${r.name || ''} ${r.description || ''} ${r.complianceFramework || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rules, matchesFacets, search]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = rules.filter(r => {
          if (g.key === 'category') return (r.category || 'CUSTOM') === opt.key;
          if (g.key === 'action')   return r.action === opt.key;
          if (g.key === 'scope')    return inferDpgScope(r) === opt.key;
          if (g.key === 'status')   return opt.key === 'enabled' ? !!r.enabled : !r.enabled;
          return false;
        }).length;
      }
    }
    return counts;
  }, [rules, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  // ── KPIs (from stats endpoint with a safe fall-back) ──────────
  const kpis = useMemo(() => ({
    total:    stats.total ?? rules.length,
    enabled:  stats.enabled ?? rules.filter(r => r.enabled).length,
    blocking: stats.blocking ?? rules.filter(r => r.action === 'BLOCK' && r.enabled).length,
    categories: Object.keys(stats.byCategory || {}).length || presentCategories.length,
  }), [stats, rules, presentCategories]);

  // ── CRUD handlers ──────────────────────────────────────────────
  const handleToggle = async (rule) => {
    try {
      const res = await api.toggleDpgRule(rule.id, !rule.enabled);
      if (res?.ok) {
        addToast({ tone: 'success', title: `Rule ${rule.enabled ? 'disabled' : 'enabled'}`, description: rule.name });
        await reload();
      } else {
        addToast({ tone: 'danger', title: 'Toggle failed', description: res?.data?.message || `Could not ${rule.enabled ? 'disable' : 'enable'} "${rule.name}"` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Toggle failed', description: e?.message });
    }
  };
  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      const res = await api.deleteDpgRule(rule.id);
      if (res?.ok) {
        addToast({ tone: 'success', title: 'Rule deleted', description: rule.name });
        await reload();
      } else {
        addToast({ tone: 'danger', title: 'Delete failed', description: res?.data?.message || `Could not delete "${rule.name}"` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message });
    }
  };
  const handleSave = async (payload) => {
    const isCreate = editing === 'new';
    try {
      const res = isCreate
        ? await api.createDpgRule(payload)
        : await api.updateDpgRule(editing.id, payload);
      if (res?.ok) {
        addToast({ tone: 'success', title: isCreate ? 'Rule created' : 'Rule updated', description: payload?.name });
        setEditing(null);
        await reload();
      } else {
        addToast({ tone: 'danger', title: isCreate ? 'Create failed' : 'Update failed', description: res?.data?.message });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: isCreate ? 'Create failed' : 'Update failed', description: e?.message });
    }
  };

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total rules"    value={kpis.total} tone="cyan" />
        <MetricCard label="Enabled"        value={kpis.enabled} tone="success" />
        <MetricCard label="Blocking rules" value={kpis.blocking} tone={kpis.blocking > 0 ? 'danger' : 'muted'} />
        <MetricCard label="Categories"     value={kpis.categories} tone="action" />
      </div>

      <Card className="px-4 py-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-v2-md bg-v2-action-soft flex items-center justify-center">
            <Shield size={18} className="text-v2-action-strong" />
          </div>
          <div className="text-[12.5px] leading-[1.55] text-v2-ink-secondary">
            <span className="font-semibold text-v2-ink">Data Protection Gate — Classification Rules.</span>{' '}
            Every DPG node evaluates these rules on every workflow this tenant runs.
            On match, the configured action fires (block / redact / tokenize / mask / log)
            before sensitive data reaches an LLM, an external system, or another node.
            System defaults are shipped by the platform; tenant rules override them per-priority.
          </div>
        </div>
      </Card>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map(c => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, description, or compliance framework…"
          aria-label="Search rules"
          className="flex-1 min-w-[240px] text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:ring-1 focus:ring-v2-action"
        />
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload} disabled={loading}>
          Refresh
        </Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setEditing('new')}>
          New Rule
        </Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Classification rule filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Shield size={20} />}
            title={rules.length === 0 ? 'No classification rules yet' : 'No rules match the current filters'}
            description={rules.length === 0
              ? 'Get started by creating your first rule, or wait for the system-default rules to seed.'
              : 'Adjust the filters on the left or clear them to see all rules.'}
            primaryAction={rules.length === 0 ? (
              <Button variant="primary" iconLeft={<Plus />} onClick={() => setEditing('new')}>
                Create your first rule
              </Button>
            ) : null}
          />
        ) : (
          <div>
            <Table
              ariaLabel="Data classification rules"
              density="comfortable"
              columns={[
                { key: 'name', header: 'Rule', sortable: true, render: (r) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-v2-ink text-[13px]">{r.name}</span>
                      {!r.enabled && (
                        <Pill tone="muted" size="sm">Off</Pill>
                      )}
                    </div>
                    {r.description && (
                      <div className="text-[11.5px] text-v2-ink-muted mt-0.5 line-clamp-1">{r.description}</div>
                    )}
                    {r.complianceFramework && (
                      <div className="text-[10.5px] font-semibold text-v2-trust-strong mt-1">{r.complianceFramework}</div>
                    )}
                  </div>
                )},
                { key: 'category', header: 'Category', render: (r) => (
                  <Pill tone="action" size="sm">{DPG_CATEGORY_LABELS[r.category] || r.category || 'Custom'}</Pill>
                )},
                { key: 'detection', header: 'Detection', render: (r) => (
                  <span className="font-mono text-[11.5px] text-v2-ink-secondary uppercase tracking-wide">
                    {r.detectionMethod || '—'}
                  </span>
                )},
                { key: 'action', header: 'Action', render: (r) => (
                  <Pill tone={DPG_ACTION_TONES[r.action] || 'muted'} size="sm">{r.action || '—'}</Pill>
                )},
                { key: 'scope', header: 'Scope', render: (r) => {
                  const s = inferDpgScope(r);
                  return <Pill tone={s === 'SYSTEM' ? 'muted' : s === 'ORG' ? 'trust' : 'action'} size="sm">
                    {s === 'SYSTEM' ? 'System default' : s === 'ORG' ? 'Org override' : 'Tenant'}
                  </Pill>;
                }},
                { key: 'sensitivity', header: 'Sensitivity', render: (r) => (
                  <SensitivityDots level={r.sensitivityLevel} />
                )},
                { key: 'actions', header: '', align: 'right', render: (r) => {
                  const isSystem = inferDpgScope(r) === 'SYSTEM';
                  return (
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="xs" onClick={() => handleToggle(r)}
                        title={r.enabled ? 'Disable rule' : 'Enable rule'}>
                        {r.enabled ? 'Disable' : 'Enable'}
                      </Button>
                      <Button variant="ghost" size="xs" iconLeft={<Edit3 size={12} />} onClick={() => setEditing(r)}
                        title="Edit rule">Edit</Button>
                      {!isSystem && (
                        <Button variant="ghost" size="xs" iconLeft={<Trash2 size={12} />} onClick={() => handleDelete(r)}
                          title="Delete rule">Delete</Button>
                      )}
                    </div>
                  );
                }},
              ]}
              rows={visibleRows}
              loading={loading}
              getRowKey={(r) => r.id}
            />
            <Pagination
              page={pg.page}
              pageSize={pg.pageSize}
              totalItems={filtered.length}
              onPageChange={pg.setPage}
              onPageSizeChange={pg.setPageSize}
              noun="rules"
            />
          </div>
        )}
      </div>

      {editing && (
        <DataClassificationRuleEditor
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/** Sensitivity 1-5 rendered as 5 vertical bars filled green→red. */
function SensitivityDots({ level }) {
  const lvl = Math.min(5, Math.max(1, Number(level) || 1));
  const tones = ['#84cc16', '#a3e635', '#facc15', '#fb923c', '#dc2626'];
  return (
    <div className="inline-flex items-center gap-1.5" title={`Sensitivity ${lvl} of 5`}>
      <div className="inline-flex gap-[2px]">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="rounded-[2px]"
            style={{
              width: 6, height: 14,
              background: i < lvl ? tones[lvl - 1] : 'var(--v2-border-subtle, #e7e5e4)',
            }} />
        ))}
      </div>
      <span className="text-[11px] font-semibold tabular-nums text-v2-ink-secondary">{lvl}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  G433 — Pattern Library Inventory (V2)
 *
 *  Replaces the legacy PatternLibraryPanel with the canonical V2 surface
 *  (MetricCard + FacetRail + Table + Slideout + EmptyState + pagination).
 *  Wires into the same backend endpoints (api.getEnforcementPatterns,
 *  getPatternStats, createPattern, updatePattern, deletePattern) and
 *  preserves all existing CRUD semantics. The create form is implemented
 *  inline using the v2 Slideout with the same validation rules as the
 *  legacy CreatePatternModal (regex validity check, JSON template check).
 *  ───────────────────────────────────────────────────────────────── */
const PATTERN_DOMAINS = [
  'SECURITY', 'ACCESS_CONTROL', 'DATA_CLASSIFICATION',
  'CHANGE_MANAGEMENT', 'OPERATIONAL', 'COMPLIANCE',
];
const PATTERN_DOMAIN_LABELS = {
  SECURITY: 'Security',
  ACCESS_CONTROL: 'Access Control',
  DATA_CLASSIFICATION: 'Data Classification',
  CHANGE_MANAGEMENT: 'Change Management',
  OPERATIONAL: 'Operational',
  COMPLIANCE: 'Compliance',
};
const PATTERN_RULE_TYPES = [
  'REGEX', 'RANGE', 'ENUM', 'APPROVAL_CHAIN', 'RATE_LIMIT',
  'COMPOSITE', 'FIELD_REQUIRED', 'TEMPORAL', 'CUSTOM_SCRIPT', 'DPG_CLASSIFICATION',
];

function PatternLibraryInventory() {
  const { addToast } = useToast();
  const [patterns, setPatterns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Domain filter is server-driven for performance — same as v1.
  const [domainFilter, setDomainFilter] = useState('ALL');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const domain = domainFilter !== 'ALL' ? domainFilter : undefined;
      const [patRes, statsRes] = await Promise.all([
        api.getEnforcementPatterns?.(domain) ?? Promise.resolve({ ok: true, data: [] }),
        api.getPatternStats?.() ?? Promise.resolve({ ok: true, data: {} }),
      ]);
      if (patRes?.ok) setPatterns(Array.isArray(patRes.data) ? patRes.data : []);
      if (statsRes?.ok) setStats(statsRes.data || {});
      if (!patRes?.ok) {
        addToast({ tone: 'danger', title: 'Load failed', description: patRes?.data?.message || 'Could not load enforcement patterns' });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Load failed', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [domainFilter, addToast]);
  useEffect(() => { reload(); }, [reload]);

  // ── Facet state (rule type + status; domain is on the toolbar) ─
  const [facets, setFacets] = useState(() => ({
    ruleType: new Set(), status: new Set(),
  }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => {
    const n = new Set(p[gKey] || []);
    if (c) n.add(oKey); else n.delete(oKey);
    return { ...p, [gKey]: n };
  });
  const clearFacets = () => setFacets({ ruleType: new Set(), status: new Set() });
  const [search, setSearch] = useState('');

  const presentRuleTypes = useMemo(() => {
    const s = new Set();
    for (const p of patterns) if (p.mapsToRuleType) s.add(p.mapsToRuleType);
    return Array.from(s);
  }, [patterns]);

  const FACET_GROUPS = useMemo(() => ([
    { key: 'ruleType', label: 'Rule type', options: presentRuleTypes.map(t => ({ key: t, label: t.replace(/_/g, ' ') })) },
    { key: 'status',   label: 'Status',    options: [
      { key: 'enabled',  label: 'Enabled' },
      { key: 'disabled', label: 'Disabled' },
    ]},
  ]), [presentRuleTypes]);

  const matchesFacets = useCallback((p) => {
    if (facets.ruleType.size > 0 && !facets.ruleType.has(p.mapsToRuleType)) return false;
    if (facets.status.size > 0) {
      const wantsOn  = facets.status.has('enabled');
      const wantsOff = facets.status.has('disabled');
      const isOn = p.enabled !== false;
      if (wantsOn && !wantsOff && !isOn) return false;
      if (wantsOff && !wantsOn && isOn) return false;
    }
    return true;
  }, [facets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return patterns.filter(p => {
      if (!matchesFacets(p)) return false;
      if (q) {
        const hay = `${p.patternName || ''} ${p.domain || ''} ${p.mapsToRuleType || ''} ${p.exampleText || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [patterns, matchesFacets, search]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = patterns.filter(p => {
          if (g.key === 'ruleType') return p.mapsToRuleType === opt.key;
          if (g.key === 'status')   return opt.key === 'enabled' ? p.enabled !== false : p.enabled === false;
          return false;
        }).length;
      }
    }
    return counts;
  }, [patterns, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g,
    options: g.options.map(o => ({
      ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false,
    })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    if (domainFilter !== 'ALL') {
      chips.push({
        groupKey: '__domain__', optionKey: domainFilter,
        label: `Domain: ${PATTERN_DOMAIN_LABELS[domainFilter] || domainFilter}`,
      });
    }
    return chips;
  }, [facets, FACET_GROUPS, domainFilter]);

  const removeChip = (chip) => {
    if (chip.groupKey === '__domain__') setDomainFilter('ALL');
    else toggleFacet(chip.groupKey, chip.optionKey, false);
  };

  // ── KPIs ──────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = patterns.length || Object.values(stats).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
    const active = patterns.filter(p => p.enabled !== false).length;
    const domains = new Set(patterns.map(p => p.domain).filter(Boolean)).size;
    const conf = patterns.length > 0
      ? (patterns.reduce((s, p) => s + (parseFloat(p.confidenceWeight) || 0), 0) / patterns.length)
      : null;
    return {
      total, active, domains,
      avgConfidence: conf == null ? '—' : conf.toFixed(2),
    };
  }, [patterns, stats]);

  // ── CRUD handlers ─────────────────────────────────────────────
  const handleCreate = async (payload) => {
    try {
      const res = await api.createPattern(payload);
      if (res?.ok) {
        addToast({ tone: 'success', title: 'Pattern created', description: payload?.patternName });
        setCreating(false);
        await reload();
      } else {
        addToast({ tone: 'danger', title: 'Create failed', description: res?.data?.message });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Create failed', description: e?.message });
    }
  };
  const handleToggle = async (p) => {
    try {
      const res = await api.updatePattern(p.id, { enabled: p.enabled === false });
      if (res?.ok) {
        addToast({ tone: 'success', title: `Pattern ${p.enabled === false ? 'enabled' : 'disabled'}`, description: p.patternName });
        await reload();
      } else {
        addToast({ tone: 'danger', title: 'Toggle failed', description: res?.data?.message || `Could not ${p.enabled === false ? 'enable' : 'disable'} "${p.patternName}"` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Toggle failed', description: e?.message });
    }
  };
  const handleDelete = async (p) => {
    if (!window.confirm(`Delete pattern "${p.patternName}"? This cannot be undone.`)) return;
    try {
      const res = await api.deletePattern(p.id);
      if (res?.ok) {
        addToast({ tone: 'success', title: 'Pattern deleted', description: p.patternName });
        await reload();
      } else {
        addToast({ tone: 'danger', title: 'Delete failed', description: res?.data?.message || `Could not delete "${p.patternName}"` });
      }
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message });
    }
  };

  const pg = usePaginationState(50);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]); // eslint-disable-line

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total patterns" value={kpis.total} tone="cyan" />
        <MetricCard label="Active"         value={kpis.active} tone="success" />
        <MetricCard label="Domains covered" value={kpis.domains} tone="trust" />
        <MetricCard label="Avg confidence" value={kpis.avgConfidence} tone="action" />
      </div>

      <Card className="px-4 py-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-v2-md bg-v2-trust-soft flex items-center justify-center">
            <BookOpen size={18} className="text-v2-trust-strong" />
          </div>
          <div className="text-[12.5px] leading-[1.55] text-v2-ink-secondary">
            <span className="font-semibold text-v2-ink">Enforcement patterns</span>{' '}
            are reusable extraction templates that map policy text to enforcement rules.
            The Policy Skill compiler matches patterns against authored policy bodies and emits
            the configured rule (REGEX, RANGE, APPROVAL_CHAIN, …) with the captured groups bound.
          </div>
        </div>
      </Card>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map(c => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => removeChip(c)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={() => { clearFacets(); setDomainFilter('ALL'); }}
            className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, domain, rule type, or example…"
          aria-label="Search patterns"
          className="flex-1 min-w-[240px] text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action focus:ring-1 focus:ring-v2-action"
        />
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
          aria-label="Filter by domain"
          className="text-[12.5px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-2.5 py-2 text-v2-ink focus:outline-none focus:border-v2-action"
        >
          <option value="ALL">All domains</option>
          {PATTERN_DOMAINS.map(d => <option key={d} value={d}>{PATTERN_DOMAIN_LABELS[d]}</option>)}
        </select>
        <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload} disabled={loading}>
          Refresh
        </Button>
        <Button variant="primary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setCreating(true)}>
          New Pattern
        </Button>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Pattern filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<BookOpen size={20} />}
            title={patterns.length === 0 ? 'No enforcement patterns yet' : 'No patterns match the current filters'}
            description={patterns.length === 0
              ? 'Create patterns to define reusable extraction templates that map policy text to enforcement rules.'
              : 'Adjust the filters on the left or clear them to see all patterns.'}
            primaryAction={patterns.length === 0 ? (
              <Button variant="primary" iconLeft={<Plus />} onClick={() => setCreating(true)}>
                Create first pattern
              </Button>
            ) : null}
          />
        ) : (
          <div>
            <Table
              ariaLabel="Enforcement patterns"
              density="comfortable"
              columns={[
                { key: 'patternName', header: 'Pattern', sortable: true, render: (p) => (
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-v2-ink text-[13px]">{p.patternName}</span>
                      {p.tenantId && <Pill tone="warning" size="sm">Custom</Pill>}
                      {p.enabled === false && <Pill tone="muted" size="sm">Disabled</Pill>}
                    </div>
                    {p.exampleText && (
                      <div className="text-[11.5px] text-v2-ink-muted italic mt-0.5 line-clamp-1">"{p.exampleText}"</div>
                    )}
                  </div>
                )},
                { key: 'domain', header: 'Domain', render: (p) => (
                  <Pill tone="action" size="sm">{PATTERN_DOMAIN_LABELS[p.domain] || p.domain || '—'}</Pill>
                )},
                { key: 'ruleType', header: 'Rule type', render: (p) => (
                  <span className="font-mono text-[11.5px] text-v2-ink-secondary uppercase tracking-wide">
                    {(p.mapsToRuleType || '—').replace(/_/g, ' ')}
                  </span>
                )},
                { key: 'confidence', header: 'Confidence', render: (p) => (
                  <span className="font-mono tabular-nums text-[12px] text-v2-ink">
                    {parseFloat(p.confidenceWeight || 0).toFixed(2)}
                  </span>
                )},
                { key: 'regex', header: 'Pattern (regex)', render: (p) => (
                  <code className="font-mono text-[11px] text-v2-ink-secondary bg-v2-page rounded px-1.5 py-0.5 max-w-[280px] truncate inline-block">
                    {p.patternRegex || '—'}
                  </code>
                )},
                { key: 'actions', header: '', align: 'right', render: (p) => (
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="xs" onClick={() => handleToggle(p)}
                      title={p.enabled === false ? 'Enable pattern' : 'Disable pattern'}>
                      {p.enabled === false ? 'Enable' : 'Disable'}
                    </Button>
                    <Button variant="ghost" size="xs" iconLeft={<Trash2 size={12} />} onClick={() => handleDelete(p)}
                      title="Delete pattern">Delete</Button>
                  </div>
                )},
              ]}
              rows={visibleRows}
              loading={loading}
              getRowKey={(p) => p.id}
            />
            <Pagination
              page={pg.page}
              pageSize={pg.pageSize}
              totalItems={filtered.length}
              onPageChange={pg.setPage}
              onPageSizeChange={pg.setPageSize}
              noun="patterns"
            />
          </div>
        )}
      </div>

      <PatternCreateSlideout
        isOpen={creating}
        onClose={() => setCreating(false)}
        onSave={handleCreate}
      />
    </div>
  );
}

/* Inline Slideout for creating a new pattern. Mirrors the validation rules
 * of the legacy CreatePatternModal (non-empty name, valid regex, valid JSON
 * extraction template) and emits the same wire shape. */
function PatternCreateSlideout({ isOpen, onClose, onSave }) {
  const [form, setForm] = useState({
    domain: 'SECURITY',
    patternName: '',
    patternRegex: '',
    mapsToRuleType: 'REGEX',
    exampleText: '',
    extractionTemplate: '{}',
    confidenceWeight: '1.0',
  });
  const [error, setError] = useState(null);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setForm({
        domain: 'SECURITY', patternName: '', patternRegex: '',
        mapsToRuleType: 'REGEX', exampleText: '',
        extractionTemplate: '{}', confidenceWeight: '1.0',
      });
    }
  }, [isOpen]);

  const submit = () => {
    setError(null);
    if (!form.patternName.trim()) { setError('Pattern name is required'); return; }
    if (!form.patternRegex.trim()) { setError('Pattern regex is required'); return; }
    try { new RegExp(form.patternRegex); }
    catch { setError('Invalid regex syntax'); return; }
    let template;
    try { template = JSON.parse(form.extractionTemplate || '{}'); }
    catch { setError('Extraction template must be valid JSON'); return; }
    onSave({
      ...form,
      extractionTemplate: template,
      confidenceWeight: parseFloat(form.confidenceWeight) || 1.0,
    });
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title="Create enforcement pattern"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit}>Create pattern</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="bg-v2-danger-soft border border-v2-danger text-v2-danger-strong rounded-v2-md px-3 py-2 text-[12.5px]">
            {error}
          </div>
        )}

        <Field label="Domain *">
          <select value={form.domain} onChange={(e) => update('domain', e.target.value)}
            className="w-full text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink focus:outline-none focus:border-v2-action">
            {PATTERN_DOMAINS.map(d => <option key={d} value={d}>{PATTERN_DOMAIN_LABELS[d]}</option>)}
          </select>
        </Field>

        <Field label="Pattern name *">
          <input value={form.patternName} onChange={(e) => update('patternName', e.target.value)}
            placeholder="e.g. Approver count threshold"
            className="w-full text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action" />
        </Field>

        <Field label="Maps to rule type *">
          <select value={form.mapsToRuleType} onChange={(e) => update('mapsToRuleType', e.target.value)}
            className="w-full text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink focus:outline-none focus:border-v2-action">
            {PATTERN_RULE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </Field>

        <Field label="Pattern regex *"
          hint="JavaScript-compatible regex. Tested with new RegExp(...) on submit.">
          <textarea value={form.patternRegex} onChange={(e) => update('patternRegex', e.target.value)}
            placeholder="(?i)approv(er|al)\\s+threshold\\s*[:=]?\\s*(\\d+)"
            rows={3}
            className="w-full font-mono text-[12.5px] bg-v2-page border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink focus:outline-none focus:border-v2-action" />
        </Field>

        <Field label="Example text"
          hint="A sample policy phrase the regex should match — used for previewing.">
          <input value={form.exampleText} onChange={(e) => update('exampleText', e.target.value)}
            placeholder="e.g. Requires approval from at least 2 reviewers"
            className="w-full text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink placeholder:text-v2-ink-muted focus:outline-none focus:border-v2-action" />
        </Field>

        <Field label="Extraction template (JSON)"
          hint='Bindings from captured groups to rule fields. Defaults to "{}".'>
          <textarea value={form.extractionTemplate} onChange={(e) => update('extractionTemplate', e.target.value)}
            rows={4}
            className="w-full font-mono text-[12.5px] bg-v2-page border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink focus:outline-none focus:border-v2-action" />
        </Field>

        <Field label="Confidence weight"
          hint="Default 1.0 — higher patterns win when more than one matches the same policy phrase.">
          <input type="number" min="0" max="10" step="0.05"
            value={form.confidenceWeight} onChange={(e) => update('confidenceWeight', e.target.value)}
            className="w-32 text-[13px] bg-v2-surface border border-v2-border-subtle rounded-v2-md px-3 py-2 text-v2-ink focus:outline-none focus:border-v2-action" />
        </Field>
      </div>
    </Slideout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1.5">
        {label}
      </label>
      {children}
      {hint && <div className="text-[11px] text-v2-ink-muted mt-1.5">{hint}</div>}
    </div>
  );
}

/* RC-81 — URL-to-internal tab-id alias map.
 *
 * Three TAB ids predate the label conventions and don't match the
 * natural URL slug an operator would guess from the visible label:
 *   · Label "Policy Skills"        → internal id 'configurations'
 *   · Label "Data Classification"  → internal id 'classification'
 *   · Label "Pattern Library"      → internal id 'patterns'
 *
 * Pre-RC-81 the URL effect rejected anything not in `TABS.map(t => t.id)`
 * so a perfectly reasonable deep-link like
 * `/policy-config?tab=data-classification` was silently ignored — the
 * landing tab stayed on whatever the persisted state held, which from
 * the operator's perspective looked broken.
 *
 * Renaming the internal ids would break every existing bookmark and
 * the persisted localStorage key. An alias map is the safer fix:
 *   · slug-form is accepted from URL
 *   · canonical internal id is what TabNav / TABS / persisted state use
 *   · `valid.includes(requested)` keeps the old ids accepted too (they
 *     ARE in TABS), so legacy bookmarks continue to work
 *
 * Keep this map in sync with TABS — if someone renames a tab label
 * later, add the matching alias here so the deep-link survives. */
const URL_TAB_ALIASES = {
  'policy-skills':       'configurations',
  'data-classification': 'classification',
  'pattern-library':     'patterns',
};

function PolicyConfigPageV2Inner() {
  const [activeTab, setActiveTab] = usePersistedState('policyConfig.tab', 'configurations');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const requested = searchParams.get('tab');
    if (!requested) return;
    // RC-81 — Resolve slug-form aliases to the canonical internal id
    // before checking validity. The alias map only fires when the
    // requested value isn't already a known canonical id, so this
    // doesn't change behaviour for existing valid links.
    const resolved = URL_TAB_ALIASES[requested] || requested;
    const valid = TABS.map(t => t.id);
    if (valid.includes(resolved) && resolved !== activeTab) setActiveTab(resolved);
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, activeTab, setActiveTab, setSearchParams]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          {/* P7J-RUN-029 — page heading was "Policy Library" which collided
            * with the "Policy Library" tab name (3rd in TABS). Operators
            * couldn't tell whether clicking the tab was a no-op since the
            * heading and tab carried identical labels. Use "Policies" for
            * the parent heading so the tab is unambiguous. */}
          <h1 className="text-[22px] font-bold tracking-tight m-0">Policies</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Configure deterministic enforcement rules, manage policy documents, and protect agent behavior.
          </p>
        </div>
      </header>

      <TabNav tabs={TABS} activeId={activeTab} onChange={setActiveTab} ariaLabel="Policies sections" />
      <div className="my-4" />

      {activeTab === 'configurations'    && <PolicySkillsInventory />}
      {/* Step 7 (Platform-Eng workflow) — OPA decision viewer. */}
      {activeTab === 'decisions'         && <PolicyDecisionsInventory />}
      {activeTab === 'policy-library'    && <PolicyLibraryInventory />}
      {activeTab === 'control-config'    && <ControlConfigInventory />}
      {activeTab === 'framework-coverage' && <FrameworkCoverageDashboard />}
      {activeTab === 'control-drift'     && <ControlDriftInventory />}
      {activeTab === 'control-scorecard' && <ControlScorecardDashboard />}
      {/* P0-1 Sprint 2 — Per-control evidence dashboard. */}
      {activeTab === 'control-evidence'  && <ControlEvidencePanel />}
      {/* G432 / G433 — Inline V2 inventories replace the legacy
          DataClassificationRulesPanel / PatternLibraryPanel imports. */}
      {activeTab === 'classification'    && <DataClassificationInventory />}
      {activeTab === 'patterns'          && <PatternLibraryInventory />}
    </div>
  );
}

export default function ResolvedPolicyConfigPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthPoliciesEnabled()) return <PolicyConfigPage {...props} />;
  return <PolicyConfigPageV2Inner {...props} />;
}

export { PolicyConfigPageV2Inner };
